import React, { useState, useEffect, useReducer, useCallback, useRef, useMemo } from 'react'
import { connect } from 'react-redux'
import { basicLayouts, emailTemplates } from './data'
import { BlockRenderer, BlockEditor, ImagePanel, ScheduleModal } from './components'
import { ContentPanel, contentFlowTitle, contentTypeMeta } from './components/ContentPanel'
import { SectionEditor } from './components/SectionEditor'
import { BrandPanel } from './components/BrandPanel'
import { TemplatePicker } from './components/TemplatePicker'
import { ClassicTemplatePicker } from './components/ClassicTemplatePicker'
import { EMAIL_TEMPLATES_V2_VISIBLE } from '../../../../config/featureFlags'
import { EmailFrame, ShadowHtml, smallImages } from './components/EmailHtml'
import { SECTIONS, SECTION_GROUPS, isSectionType, sectionDefaults } from './email/sections'
import { renderEmail, renderBlockHtml, themeForBlocks, isThemedEmail, ensureFontsLoaded } from './email/render'
import { themeSpecFromBlocks, withThemeSpec, loadBrandKit, saveBrandKit, kitFromSalon, marketFor, detectLogoShape } from './email/theme'
import { findTemplate, blankTemplate, IMG } from './data/templates'
import { buildTemplateBlocks, brandLookOf, BRAND_LOOK } from './email/apply'
import { salonLinks, resolveLocalLinks } from './email/links'
import { emailHtmlPayload as buildEmailHtmlPayload, designSavePayload } from './email/payload'
import { blocksFromWords, blocksFromClassic } from '../plans/data/emailFor'
import { hydrateBrandKit, pushBrandKit } from '../brand/brandKitSync'
import { salonService } from '../../../../_services/salon.service'
import { hapticTick, hapticSuccess } from '../../../../_utils/haptics'
import { designToContent } from './components/voucherDesigns'
import { loadBusinessDetails, saveBusinessDetails, isBusinessDetailsComplete, companyDetailsLine, hydrateBusinessDetails } from '../../../marketing/businessDetails'
import { loadUserTemplates, saveUserTemplate, deleteUserTemplate, hydrateUserTemplates, templateTrade, withTemplateTrade } from './data/userTemplates'
import BusinessDetailsForm from '../../../marketing/BusinessDetailsForm'
import ClientPicker from '../../components/ClientPicker'
import { marketingApiService } from '../../services/marketingApiService'
import { marketingAction } from '../../../../_actions/marketing.actions'
import SmsWalletTopupModal from '../sms-composer/SmsWalletTopupModal'
import { formatPence, formatUnitPence, walletBalancePence, messagePrices } from '../../../pricing/money'
import CreatorFrame from '../../../shared/CreatorFrame'
import { useSendSites, SendLocationsCard, SendLocationsResults, broadcastToSites } from '../../components/SendLocations'
import './EmailBuilder.css'
import { codeEmail } from '../../../discounts/pickers/codeMessages'

// Generate unique IDs
const generateId = () => `block-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`

// ─── Undo / redo history over the email blocks ───
// Mirrors the gift-card designer: rapid successive changes within COALESCE_MS
// collapse into one history entry so a single undo reverts a whole gesture.
// setEmailBlocks keeps the (updater | value) signature of useState, so every
// existing call site is unchanged.
const COALESCE_MS = 450
const HISTORY_LIMIT = 100
const blockHistoryReducer = (h, action) => {
  switch (action.type) {
    case 'set': {
      const next = typeof action.updater === 'function' ? action.updater(h.present) : action.updater
      if (next === h.present) return h
      const coalesce = action.now - h.lastAt < COALESCE_MS
      const past = coalesce ? h.past : [...h.past, h.present].slice(-HISTORY_LIMIT)
      return { past, present: next, future: [], lastAt: action.now }
    }
    case 'undo': {
      if (!h.past.length) return h
      return { past: h.past.slice(0, -1), present: h.past[h.past.length - 1], future: [h.present, ...h.future], lastAt: 0 }
    }
    case 'redo': {
      if (!h.future.length) return h
      return { past: [...h.past, h.present], present: h.future[0], future: h.future.slice(1), lastAt: 0 }
    }
    // Swap the present without an undo step (the salon's brand arriving
    // after a template opened is not something to undo back out of)
    case 'replace':
      return { ...h, present: action.present }
    default:
      return h
  }
}

// ─── Friendly labels ───
const BLOCK_LABELS = {
  text: 'Text', image: 'Image', logo: 'Logo', button: 'Button', divider: 'Divider',
  spacer: 'Spacer', columns: 'Columns', social: 'Social', review: 'Review',
  service: 'Service', team: 'Team', product: 'Product', voucher: 'Gift Voucher', footer: 'Footer',
}
const blockLabel = (type) => {
  if (SECTIONS[type]) return SECTIONS[type].label
  if (BLOCK_LABELS[type]) return BLOCK_LABELS[type]
  if (type && type.startsWith('layout-')) {
    // "layout-editorial-hero" -> "Editorial Hero"
    return type.replace('layout-', '').split('-')
      .map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ')
  }
  return 'Block'
}
const SECTION_LABELS = { layouts: 'Layouts', text: 'Text', image: 'Image', button: 'Button', content: 'Content', elements: 'Elements', brand: 'Brand' }
const SECTION_LABELS_V2 = { ...SECTION_LABELS, layouts: 'Sections' }

// ─── Rail + toolbar icons (1.7 stroke, match the gift-card designer) ───
const ic = { width: 22, height: 22, viewBox: '0 0 24 24', fill: 'none', stroke: 'currentColor', strokeWidth: 1.7, strokeLinecap: 'round', strokeLinejoin: 'round', 'aria-hidden': true }
const RailLayouts = () => (<svg {...ic}><rect x="3" y="3" width="7" height="7" rx="1.5"/><rect x="14" y="3" width="7" height="7" rx="1.5"/><rect x="3" y="14" width="7" height="7" rx="1.5"/><rect x="14" y="14" width="7" height="7" rx="1.5"/></svg>)
const RailText = () => (<svg {...ic}><path d="M5 6h14"/><path d="M12 6v13"/><path d="M9 19h6"/></svg>)
const RailImage = () => (<svg {...ic}><rect x="3.5" y="4.5" width="17" height="14" rx="2"/><circle cx="8.5" cy="10" r="1.3" fill="currentColor" stroke="none"/><path d="M3.5 16l5-5 4 4 3.5-3.5 4.5 4"/></svg>)
const RailButton = () => (<svg {...ic}><rect x="3" y="8" width="18" height="8" rx="4"/><path d="M8 12h8"/></svg>)
const RailContent = () => (<svg {...ic}><rect x="3.5" y="4" width="17" height="6" rx="1.5"/><path d="M3.5 14h10M3.5 18h7"/></svg>)
const RailElements = () => (<svg {...ic}><circle cx="7" cy="7" r="3.5"/><rect x="13" y="13" width="7" height="7" rx="1.5"/><path d="M14 4h6M17 4v6"/></svg>)
const RAIL = [
  { id: 'layouts', label: 'Layouts', Icon: RailLayouts },
  { id: 'text', label: 'Text', Icon: RailText },
  { id: 'image', label: 'Image', Icon: RailImage },
  { id: 'button', label: 'Button', Icon: RailButton },
  { id: 'content', label: 'Content', Icon: RailContent },
  { id: 'elements', label: 'Elements', Icon: RailElements },
]
const RailBrand = () => (<svg {...ic}><path d="M12 3a9 9 0 1 0 0 18c1.1 0 1.8-.8 1.8-1.8 0-.5-.2-.9-.5-1.2-.3-.3-.5-.7-.5-1.2 0-1 .8-1.8 1.8-1.8H16a5 5 0 0 0 5-5C21 6.6 17 3 12 3z"/><circle cx="7.5" cy="11" r="1.2" fill="currentColor" stroke="none"/><circle cx="10" cy="7" r="1.2" fill="currentColor" stroke="none"/><circle cx="14.5" cy="7" r="1.2" fill="currentColor" stroke="none"/></svg>)
// Themed emails (the new templates) get Brand and email-native sections;
// classic emails keep the classic rail.
const RAIL_V2 = [
  { id: 'brand', label: 'Brand', Icon: RailBrand },
  { id: 'layouts', label: 'Sections', Icon: RailLayouts },
  { id: 'text', label: 'Text', Icon: RailText },
  { id: 'image', label: 'Image', Icon: RailImage },
  { id: 'button', label: 'Button', Icon: RailButton },
  { id: 'content', label: 'Content', Icon: RailContent },
]
const INSERT_ITEMS_V2 = {
  text: [
    { label: 'Heading and text', desc: 'A small heading, a title and a paragraph', type: 's-text', overrides: { eyebrow: 'Some news', heading: 'A heading that says it all', body: 'Write something lovely for your clients.' } },
    { label: 'Just text', desc: 'A paragraph or two, like a letter', type: 's-text', overrides: { eyebrow: '', heading: '', body: 'Write something lovely for your clients.', align: 'left' } },
    { label: 'Sign-off', desc: 'Close with your name and a photo', type: 's-signoff' },
    { label: 'Announcement bar', desc: 'A slim strip for the headline news', type: 's-banner' },
  ],
  image: [
    { label: 'Image', desc: 'A photo from edge to edge', type: 's-image', overrides: { width: 'full' }, picker: 'src' },
    { label: 'Framed image', desc: 'A photo with space around it', type: 's-image', overrides: { width: 'inset' }, picker: 'src' },
    { label: 'Gallery', desc: 'Two or three photos in a row', type: 's-gallery' },
    { label: 'Image and text', desc: 'A photo beside a short story', type: 's-split' },
  ],
  button: [
    { label: 'Button', desc: 'In your brand colour', type: 's-button', overrides: { style: 'theme' } },
    { label: 'Outline button', desc: 'Quieter, for a second action', type: 's-button', overrides: { style: 'outline', text: 'See our prices' } },
    { label: 'Text link', desc: 'An underlined link with an arrow', type: 's-button', overrides: { style: 'link', text: 'See what’s new' } },
  ],
}

// A section's thumbnail in the Sections panel: its real HTML at half size,
// with photos from the salon's own world so the choice is easy to picture.
// (An inserted section starts with an "Add an image" space instead.)
const THUMB_IMAGES = {
  hair: [IMG.hairStylistJoy, IMG.hairBlondeWaves, IMG.hairCurlsJoy, IMG.hairCopper, IMG.portraitBun],
  barber: [IMG.barberClient, IMG.barberFade, IMG.barberBeardTrim, IMG.barberTowel, IMG.barberBeardProfile],
  beauty: [IMG.facialClinic, IMG.nailsNude, IMG.lashLift, IMG.hotStones, IMG.skincareSmile],
}
const thumbContent = (type, market) => {
  const [hero, a, b, c, person] = THUMB_IMAGES[market] || THUMB_IMAGES.hair
  const d = sectionDefaults(type)
  switch (type) {
    case 's-hero': return { ...d, image: hero }
    case 's-image': return { ...d, src: a }
    case 's-split': return { ...d, image: person }
    case 's-gallery': return { ...d, images: (d.images || []).map((g, i) => ({ ...g, src: [a, b, c][i] })) }
    case 's-cards': return { ...d, items: (d.items || []).map((it, i) => ({ ...it, image: [a, b, c][i] })) }
    case 's-team': return { ...d, members: (d.members || []).map((m, i) => ({ ...m, photo: [person, b, c][i] })) }
    default: return d
  }
}
const SectionThumb = ({ type, theme, market }) => {
  const html = useMemo(() => smallImages(renderBlockHtml({ type, content: thumbContent(type, market) }, theme, { builder: true })), [type, theme, market])
  const ref = useRef(null)
  const [h, setH] = useState(120)
  useEffect(() => {
    const el = ref.current
    if (!el) return undefined
    const measure = () => { const v = el.offsetHeight; if (v > 0) setH(Math.min(Math.round(v * 0.5), 150)) }
    measure()
    if (typeof ResizeObserver === 'undefined') return undefined
    const ro = new ResizeObserver(measure)
    ro.observe(el)
    return () => ro.disconnect()
  }, [html])
  return (
    <span className="eb-sec-thumb" style={{ height: h, display: 'block' }} aria-hidden>
      <span ref={ref} className="eb-sec-thumb-inner" style={{ display: 'block', transform: 'scale(0.5)' }}>
        <ShadowHtml html={html} />
      </span>
    </span>
  )
}

// Salon content (services, products, vouchers, reviews, team) as sections.
const priceText = (item) => {
  const n = Number(item.price)
  if (item.price === '' || item.price == null || Number.isNaN(n)) return ''
  return `${item.currency || '£'}${n % 1 ? n.toFixed(2) : n}`
}
const sectionFromContent = (type, item, style) => {
  const cardLink = type === 'product' ? { link: 'Shop now', url: item.url || '{{ShopLink}}' } : { link: 'Book now', url: '{{BookingLink}}' }
  switch (type) {
    case 'service':
    case 'product': {
      const detail = [item.duration, priceText(item)].filter(Boolean).join(' · ')
      if (style === 'minimal') {
        return { type: 's-menu', content: { ...sectionDefaults('s-menu'), eyebrow: '', heading: '', items: [{ name: item.name, detail: item.duration || '', price: priceText(item) }], note: '', button: cardLink.link, buttonUrl: cardLink.url } }
      }
      if (style === 'stacked') {
        return { type: 's-cards', content: { ...sectionDefaults('s-cards'), eyebrow: '', heading: '', style: 'card', imageShape: 'landscape', items: [{ image: item.image || '', title: item.name, text: item.description || '', price: priceText(item), ...cardLink }] } }
      }
      return { type: 's-split', content: { ...sectionDefaults('s-split'), image: item.image || '', imageAlt: item.name, eyebrow: type === 'service' ? (item.category || 'Treatment') : 'In the shop', heading: item.name, body: [item.description, detail].filter(Boolean).join('\n\n'), button: cardLink.link, buttonUrl: cardLink.url } }
    }
    case 'voucher': {
      const money = (v) => `${item.currency || '£'}${v}`
      return { type: 's-voucher', content: { ...sectionDefaults('s-voucher'), eyebrow: 'Gift voucher', value: item.value ? money(item.value) : `From ${money(item.fromPrice)}`, title: item.name || 'A gift they’ll love', note: item.expiry ? `Valid for ${item.expiry}` : 'Delivered by email in seconds', button: 'Buy a gift voucher', buttonUrl: item.url || '{{GiftCardLink}}' } }
    }
    case 'review':
      return { type: 's-quote', content: { ...sectionDefaults('s-quote'), quote: item.text || '', name: item.author || '', detail: item.service || '', rating: Number(item.rating) >= 5 ? 5 : Number(item.rating) >= 4 ? 4 : 0 } }
    case 'team':
      return { type: 's-team', content: { ...sectionDefaults('s-team'), layout: 'spotlight', eyebrow: 'Meet the team', heading: '', members: [{ photo: item.image || '', name: item.name || '', role: item.title || '', bio: item.bio || '' }] } }
    default:
      return null
  }
}

// Things worth a second look before a themed email goes out.
const preSendChecks = (blocks, subject) => {
  const issues = []
  if (!String(subject || '').trim()) issues.push('Add a subject line. It’s the first thing clients see.')
  const empty = []
  const dataImages = []
  ;(blocks || []).forEach((b) => {
    const c = b.content || {}
    const label = SECTIONS[b.type] ? SECTIONS[b.type].label : 'A block'
    if (c.button && !String(c.buttonUrl || '').trim()) empty.push(`“${c.button}”`)
    if (b.type === 's-button' && !String(c.url || '').trim()) empty.push(`“${c.text || 'Button'}”`)
    ;(c.items || []).forEach(it => { if (it && it.link && !String(it.url || '').trim()) empty.push(`“${it.link}”`) })
    const images = [c.image, c.src, c.photo, ...(c.items || []).map(i => i && i.image), ...(c.images || []).map(i => i && i.src), ...(c.members || []).map(m => m && m.photo)]
    if (images.some(u => typeof u === 'string' && u.startsWith('data:'))) dataImages.push(label)
  })
  if (empty.length) issues.push(`${empty.join(', ')} ${empty.length === 1 ? 'has' : 'have'} no link yet, so ${empty.length === 1 ? 'it goes' : 'they go'} nowhere. Add a link or remove the button.`)
  if (dataImages.length) issues.push('An image didn’t finish uploading. Pick it again so it shows in every inbox.')
  return issues
}

// The parts of a brand kit that belong on an email (the look stays the
// template's own).
const brandKitSpec = (kit) => {
  const out = {}
  ;['accent', 'logo', 'logoWidth', 'logoShape', 'logoShapeFor', 'name', 'display', 'text', 'button', 'radius'].forEach(k => {
    if (kit && kit[k] !== undefined && kit[k] !== null && kit[k] !== '') out[k] = kit[k]
  })
  return out
}

const tIc = { width: 16, height: 16, viewBox: '0 0 24 24', fill: 'none', stroke: 'currentColor', strokeWidth: 2, strokeLinecap: 'round', strokeLinejoin: 'round', 'aria-hidden': true }
const IconUp = () => (<svg {...tIc}><path d="M12 19V5M5 12l7-7 7 7"/></svg>)
const IconDown = () => (<svg {...tIc}><path d="M12 5v14M19 12l-7 7-7-7"/></svg>)
const IconCopy = () => (<svg {...tIc}><rect x="9" y="9" width="11" height="11" rx="2"/><path d="M5 15V5a2 2 0 0 1 2-2h10"/></svg>)
const IconTrash = () => (<svg {...tIc}><path d="M3 6h18M8 6V4a1 1 0 0 1 1-1h6a1 1 0 0 1 1 1v2m2 0v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6"/></svg>)

// Insert-row picker items per rail section. Layouts are handled separately
// (they render the layout thumbnails grid).
const INSERT_ITEMS = {
  text: [
    { label: 'Heading', desc: 'Large, bold title', type: 'text', overrides: { text: 'Heading', fontSize: '28', bold: true, align: 'center' } },
    { label: 'Subheading', desc: 'Medium emphasis', type: 'text', overrides: { text: 'Subheading', fontSize: '20', bold: true, align: 'center' } },
    { label: 'Body text', desc: 'A paragraph of copy', type: 'text', overrides: { text: 'Write something lovely for your clients.', fontSize: '16', align: 'left' } },
  ],
  image: [
    { label: 'Image', desc: 'Upload a photo', type: 'image', picker: 'image' },
    { label: 'Logo', desc: 'Your salon logo', type: 'logo', picker: 'image' },
  ],
  button: [
    { label: 'Button', desc: 'A tappable call to action', type: 'button' },
  ],
  // 'content' is handled by ContentPanel's stepped flow (type -> style -> item)
  elements: [
    { label: 'Divider', desc: 'A thin separator line', type: 'divider' },
    { label: 'Spacer', desc: 'Vertical breathing room', type: 'spacer' },
    // 'columns' removed: it has no editor, so it inserted a permanently
    // empty block. Reinstate alongside a proper column editor.
    { label: 'Social', desc: 'Social media icons', type: 'social' },
  ],
}

// Blank starting point: just the mandatory compliance footer
const BLANK_TEMPLATE = {
  id: 'blank',
  name: 'Untitled email',
  category: 'Blank',
  subject: '',
  blocks: [
    { id: 'text-1', type: 'text', content: { text: 'Start writing your email here...', align: 'left', fontSize: '15', textColor: '#1a1a1a' } },
    { id: 'footer-1', type: 'footer', content: { text: '© 2026 {{SalonName}}. All rights reserved.' } }
  ]
}

const CampaignComposerComponent = ({ onBack, onSave, dispatch, user, initialTemplateId, mode, initialBlocks, initialSubject, onSaveDesign, editCampaign, initialOfferCode }) => {
  // 'design' mode: Template -> Design only, ends with Save design (used by
  // automations); default mode is the full campaign wizard
  const isDesignMode = mode === 'design'

  // Salon-built templates + resumable drafts shown on the template step
  const [userTemplates, setUserTemplates] = useState(() => loadUserTemplates(localStorage.getItem('salonId')))
  // Hydrate templates + business details from the backend on mount (the
  // localStorage stores are caches now).
  useEffect(() => {
    let alive = true
    const salonId = localStorage.getItem('salonId')
    hydrateUserTemplates(salonId).then((list) => { if (alive) setUserTemplates(list) })
    hydrateBusinessDetails(salonId)
    return () => { alive = false }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])
  const [drafts, setDrafts] = useState([])
  const [editingCampaignId, setEditingCampaignId] = useState(null)
  const [tplSavedFlash, setTplSavedFlash] = useState(false)
  // Step management: 0 = Template, 1 = Build, 2 = Audience, 3 = Review, 4 = Success
  const [step, setStep] = useState(0)

  // Campaign state
  const [subject, setSubject] = useState('')
  const [campaignName, setCampaignName] = useState('')
  const [savingDraft, setSavingDraft] = useState(false)

  // ─── Sender identity (Fresha parity) ───
  // The email always sends from Beu's own authenticated marketing domain; these
  // two fields only customise what recipients SEE: the inbox display name and
  // where a reply lands. Both optional — the backend defaults the sender name to
  // the salon name when blank. Salon identity comes from the Redux user store,
  // the same source the SMS composer uses (state.user.userInfo.hairdresser.salon).
  const salonNameDefault = user?.userInfo?.hairdresser?.salon?.salonName || user?.userInfo?.hairdresser?.salon?.name || ''
  const salonEmailDefault = user?.userInfo?.email || ''
  const [senderName, setSenderName] = useState('')
  const [replyToEmail, setReplyToEmail] = useState('')
  // Seed the defaults once the Redux user has hydrated, unless the salon has
  // already typed something (or an edit prefill has run). Editing an existing
  // campaign overrides these from the loaded campaign in the editCampaign effect.
  useEffect(() => {
    setSenderName(prev => (prev ? prev : salonNameDefault))
    setReplyToEmail(prev => (prev ? prev : salonEmailDefault))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [salonNameDefault, salonEmailDefault])
  // Light, non-blocking reply-to check: empty is fine (BE falls back to the
  // salon email); if typed, it should look like an address. Never gates send.
  const replyToInvalid = replyToEmail.trim().length > 0 && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(replyToEmail.trim())
  // The two fields as they go into every campaign payload (create/update/draft).
  const senderIdentityPayload = () => ({
    senderName: senderName.trim(),
    replyToEmail: replyToEmail.trim(),
  })
  // Blocks live behind an undo/redo history (same chrome as the gift-card designer).
  const [blockHistory, dispatchBlocks] = useReducer(blockHistoryReducer, { past: [], present: [], future: [], lastAt: 0 })
  const emailBlocks = blockHistory.present
  const setEmailBlocks = useCallback((updater) => dispatchBlocks({ type: 'set', updater, now: Date.now() }), [])
  const undo = useCallback(() => dispatchBlocks({ type: 'undo' }), [])
  const redo = useCallback(() => dispatchBlocks({ type: 'redo' }), [])
  const canUndo = blockHistory.past.length > 0
  const canRedo = blockHistory.future.length > 0
  const [selectedBlockId, setSelectedBlockId] = useState(null)
  const [previewMode, setPreviewMode] = useState('desktop')

  // ─── Themed emails: brand kit, market, salon links, theme ───
  const brandSalonId = localStorage.getItem('salonId')
  const hairdresser = user?.userInfo?.hairdresser
  const links = useMemo(() => salonLinks(hairdresser), [hairdresser])
  // Hair, barber or beauty: the salon's business type picks the templates
  // shown first (the picker can switch)
  const [market, setMarket] = useState(() => marketFor(hairdresser?.salon?.businessType))
  const [savedKit, setSavedKit] = useState(() => loadBrandKit(brandSalonId))
  const [salonKit, setSalonKit] = useState(null)
  const [salonAddress, setSalonAddress] = useState('')
  // The salon record (undefined while loading, null if it failed): its
  // photos feed the image picker's Salon tab
  const [salonRecord, setSalonRecord] = useState(undefined)
  const [kitSavedFlash, setKitSavedFlash] = useState(false)
  useEffect(() => {
    if (!brandSalonId) { setSalonRecord(null); return undefined }
    let alive = true
    salonService.getSalonSettings(brandSalonId)
      .then((res) => {
        const d = res && res.data
        const rd = d && (d.responseData || d)
        const salon = rd && rd.data && typeof rd.data === 'object' ? rd.data : null
        if (!alive) return
        setSalonRecord(salon)
        if (!salon) return
        setSalonKit(kitFromSalon(salon))
        // The salon's saved brand from the server (refreshes this device)
        const serverKit = hydrateBrandKit(brandSalonId, salon)
        if (serverKit) setSavedKit(serverKit)
        setSalonAddress(salon.formattedAddress || '')
        setMarket(m => m || marketFor(salon.businessType))
      })
      .catch(() => { if (alive) setSalonRecord(null) })
    return () => { alive = false }
  }, []) // eslint-disable-line react-hooks/exhaustive-deps
  // The brand every new email starts with: Settings > Email branding, with
  // anything saved from the Brand panel on top.
  const brandKit = useMemo(() => ({ ...(salonKit || {}), ...(savedKit || {}) }), [salonKit, savedKit])
  const previewSample = useMemo(() => ({
    FirstName: 'Sophie',
    SalonName: brandKit.name || salonNameDefault || 'Your salon',
    SalonAddress: salonAddress || '12 High Street, Bristol BS1 4DJ',
  }), [brandKit, salonNameDefault, salonAddress])
  // A themed email (made from the new templates) carries its look on the
  // footer; everything else is a classic email and keeps the classic builder.
  const isV2 = isThemedEmail(emailBlocks)
  const themeSpec = isV2 ? themeSpecFromBlocks(emailBlocks) : null
  const themeKey = JSON.stringify(themeSpec || null)
  const emailTheme = useMemo(() => (isV2 ? themeForBlocks(emailBlocks) : null), [isV2, themeKey]) // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { if (emailTheme) ensureFontsLoaded(emailTheme) }, [emailTheme])
  const applyThemeSpec = (spec) => setEmailBlocks(blocks => withThemeSpec(blocks, spec))
  const footerBlock = emailBlocks.find(b => b.type === 'footer')
  const preheader = (footerBlock && footerBlock.content && footerBlock.content.preheader) || ''
  const setPreheader = (v) => setEmailBlocks(blocks => blocks.map(b => (b.type === 'footer' ? { ...b, content: { ...b.content, preheader: v } } : b)))
  // The blocks exactly as a template was applied, so the salon's brand can
  // still land on them if it arrives a moment later (quick starts open the
  // builder before Settings has answered).
  const appliedRef = useRef(null)
  useEffect(() => {
    const applied = appliedRef.current
    if (!salonKit || !applied || applied.blocks !== emailBlocks || !isThemedEmail(emailBlocks)) return
    const next = withThemeSpec(emailBlocks, { ...(themeSpecFromBlocks(emailBlocks) || {}), ...brandKitSpec(brandKit) })
    appliedRef.current = { blocks: next }
    dispatchBlocks({ type: 'replace', present: next })
  }, [salonKit]) // eslint-disable-line react-hooks/exhaustive-deps
  // A logo nobody has picked a shape for gets one from the image itself
  // (wide logos as they are, square photos in a circle). logoShapeFor marks
  // which image the shape belongs to; not an undo step.
  const emailBlocksRef = useRef(emailBlocks)
  emailBlocksRef.current = emailBlocks
  const specLogo = themeSpec && themeSpec.logo
  const specLogoFor = themeSpec && themeSpec.logoShapeFor
  useEffect(() => {
    if (!specLogo || specLogoFor === specLogo) return
    let live = true
    detectLogoShape(specLogo).then((shape) => {
      const current = emailBlocksRef.current
      const spec = themeSpecFromBlocks(current)
      if (!live || !spec || spec.logo !== specLogo) return
      const next = withThemeSpec(current, { ...spec, logoShape: shape === 'natural' ? undefined : shape, logoShapeFor: specLogo })
      // Still "as applied", so a salon brand arriving later can land on it
      if (appliedRef.current && appliedRef.current.blocks === current) appliedRef.current = { blocks: next }
      dispatchBlocks({ type: 'replace', present: next })
    })
    return () => { live = false }
  }, [specLogo, specLogoFor]) // eslint-disable-line react-hooks/exhaustive-deps
  // Review shows the real email, as a client would get it
  const [reviewDevice, setReviewDevice] = useState('desktop')
  const reviewHtml = useMemo(() => (
    step === 3 && isV2 ? renderEmail(resolveLocalLinks(emailBlocks, links), { subject, sample: previewSample }) : ''
  ), [step, isV2, emailBlocks, links, subject, previewSample])
  const reviewChecks = useMemo(() => (step === 3 && isV2 ? preSendChecks(emailBlocks, subject) : []), [step, isV2, emailBlocks, subject])

  // Audience state
  const [audienceSegments, setAudienceSegments] = useState([])
  const [loadingAudience, setLoadingAudience] = useState(true)
  const [selectedSegments, setSelectedSegments] = useState([])
  // Send confirmation happens in a popup on the Send click (an in-page
  // checkbox at the bottom of a long review silently disabled the header
  // buttons, which read as broken, especially on mobile).
  const [showSendConfirm, setShowSendConfirm] = useState(false)
  // Mobile pins Send/Schedule to the bottom of the review step instead of
  // squeezing them into the top bar.
  const [isMobileView, setIsMobileView] = useState(typeof window !== 'undefined' && window.innerWidth < 768)
  useEffect(() => {
    const onResize = () => setIsMobileView(window.innerWidth < 768)
    window.addEventListener('resize', onResize)
    return () => window.removeEventListener('resize', onResize)
  }, [])
  // Forced flow: business details must exist before anything sends (UK
  // sender-identification rules); the gate completes them inline
  const [showBizGate, setShowBizGate] = useState(false)
  const [bizDraft, setBizDraft] = useState(null)
  const [afterBizGate, setAfterBizGate] = useState(null)
  const [isScheduled, setIsScheduled] = useState(false)
  // One combined audience: an optional smart segment, plus optional
  // hand-picked clients on top. Either alone is a valid send.
  const [selectedClients, setSelectedClients] = useState([])
  // Consent-aware reachable count for a hand-picked audience — the BE drops
  // unsubscribed/suppressed picks at send time, so tell the salon up front
  // instead of letting the list quietly shrink.
  const [reachableCount, setReachableCount] = useState(null)
  useEffect(() => {
    if (selectedClients.length === 0) { setReachableCount(null); return undefined }
    let alive = true
    const t = setTimeout(() => {
      marketingApiService.estimateCost(dispatch, {
        type: 'email',
        audienceSegment: 'custom',
        clientIds: selectedClients.map(c => c.id),
      })
        .then(res => { if (alive && res && res.recipientCount !== undefined) setReachableCount(res.recipientCount) })
        .catch(() => {})
    }, 300)
    return () => { alive = false; clearTimeout(t) }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedClients])

  // Modal states
  const [showImagePicker, setShowImagePicker] = useState(false)
  // When set, the picked image is written to this content field instead of the type-based default
  const [imagePickerField, setImagePickerField] = useState(null)
  // Stepped content flow rendered inside the side panel:
  // null | { type, step: 'style' | 'pick', style, mode: 'insert' | 'swap', targetId }
  const [contentFlow, setContentFlow] = useState(null)
  const [showScheduleModal, setShowScheduleModal] = useState(false)
  // Gift-card-style editor: which rail section's panel is open, and which
  // block (if any) is being edited in the panel.
  const [railSection, setRailSection] = useState(null)
  const [editingBlockId, setEditingBlockId] = useState(null)
  // Mobile editing is split-screen: the sheet takes the bottom half, the
  // canvas the top, and the edited block is scrolled into that top strip so
  // changes are visible as they're made. The peek chevron collapses the
  // sheet to its header for a full-canvas check (layout shifts, spacing).
  const [panelCollapsed, setPanelCollapsed] = useState(false)
  // The device toggle hides while scrolling down and returns on scroll up,
  // so it stops costing canvas space the moment it's not wanted.
  const [ctxHidden, setCtxHidden] = useState(false)

  // AI generation

  // Sending state
  const [isSending, setIsSending] = useState(false)
  const [sendError, setSendError] = useState(null)

  // Multi-location (group owners only): other locations this also goes out
  // from, each as its own send there. Default: only here.
  const sendSites = useSendSites()
  const [otherSiteIds, setOtherSiteIds] = useState([])
  // { campaignId, sendNow, ids, results, error, retrying, after } once a send here has gone
  const [siteFan, setSiteFan] = useState(null)

  // Marketing allowance (email is free up to the monthly pool, then 1 credit each)
  const [emailSpendable, setEmailSpendable] = useState(null)
  const [walletCredits, setWalletCredits] = useState(null)
  const [creditCosts, setCreditCosts] = useState(null)
  // The settings row as it came, for pounds (balancePence, else credits × 2p).
  const [walletRow, setWalletRow] = useState(null)
  const [autoRecharge, setAutoRecharge] = useState(null)
  const [showTopup, setShowTopup] = useState(false)

  const loadAllowance = useCallback(() => {
    dispatch(marketingAction.getSettings())
      .then(res => {
        const r = res?.data?.responseData?.result
        if (!r) return
        if (r.marketingEmailSpendable !== undefined) setEmailSpendable(r.marketingEmailSpendable)
        if (r.walletCredits !== undefined) setWalletCredits(r.walletCredits)
        if (r.creditCosts) setCreditCosts(r.creditCosts)
        setWalletRow(r)
        if (r.autoRecharge) setAutoRecharge(r.autoRecharge)
      })
      .catch(() => {})
  }, [dispatch])
  useEffect(() => { loadAllowance() }, [loadAllowance])

  // Mobile responsive
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768)
  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth < 768)
    window.addEventListener('resize', onResize)
    return () => window.removeEventListener('resize', onResize)
  }, [])

  // Drafts are real campaigns with status draft; resuming one updates it
  // rather than creating a duplicate
  useEffect(() => {
    if (isDesignMode) return undefined
    let alive = true
    marketingApiService.getCampaigns(dispatch, { type: 'email' })
      .then(list => {
        if (!alive) return
        setDrafts((list || []).filter(c => c.status === 'draft').map(c => ({
          id: c.id,
          name: c.name || 'Untitled campaign',
          subject: c.subject || '',
          blocks: Array.isArray(c._raw?.emailBlocks) ? c._raw.emailBlocks : [],
        })))
      })
      .catch(() => {})
    return () => { alive = false }
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  // Design mode with an existing design lands straight in the builder
  useEffect(() => {
    if (isDesignMode && initialBlocks && initialBlocks.length) {
      // Saved with the old blocks (journey recipes, older reminders): open the
      // same words in the salon's new template, as campaigns do
      let blocks = initialBlocks
      if (EMAIL_TEMPLATES_V2_VISIBLE && !isThemedEmail(blocks)) {
        let themed = []
        try { themed = blocksFromClassic(blocks, initialSubject, { market: market || 'hair', kit: brandKit, links }) } catch (e) { themed = [] }
        if (themed.length) blocks = themed.map(b => ({ ...b, id: generateId() }))
      }
      setEmailBlocks(blocks)
      if (initialSubject) setSubject(initialSubject)
      setStep(1)
    }
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  // Opened from the campaigns hub to edit an existing draft/scheduled campaign —
  // hydrate it and land in the builder, updating it rather than creating a copy.
  useEffect(() => {
    if (isDesignMode || !editCampaign || !editCampaign.id) return
    const saved = Array.isArray(editCampaign._raw?.emailBlocks) ? editCampaign._raw.emailBlocks : []
    // Words with no design yet (a draft Bailey wrote in the chat): open it in
    // the salon's template with those words in, never a blank email
    let blocks = saved
    if (!blocks.length) {
      try {
        blocks = blocksFromWords({
          subject: editCampaign.subject,
          message: editCampaign.message || editCampaign._raw?.message,
          audienceSegment: editCampaign._raw?.audienceSegment || editCampaign.audience,
        }, { market: market || 'hair', kit: brandKit, links })
      } catch (e) { blocks = [] }
    }
    setCampaignName(editCampaign.name || 'Untitled campaign')
    setSubject(editCampaign.subject || '')
    // Pre-fill sender identity from the saved campaign (the API now returns
    // senderName / replyToEmail). Fall back to the salon defaults when absent.
    setSenderName(editCampaign.senderName || editCampaign._raw?.senderName || salonNameDefault)
    setReplyToEmail(editCampaign.replyToEmail || editCampaign._raw?.replyToEmail || salonEmailDefault)
    setEmailBlocks((blocks.length ? blocks : BLANK_TEMPLATE.blocks).map(b => ({ ...b, id: generateId() })))
    setEditingCampaignId(editCampaign.id)
    const seg = editCampaign._raw?.audienceSegment
    const pickedIds = editCampaign._raw?.audienceFilters?.clientIds
    if (seg === 'custom' && Array.isArray(pickedIds) && pickedIds.length) {
      // Restore the hand-picked audience so an edit + re-save keeps the
      // recipients (names hydrate when the picker list loads).
      setSelectedClients(pickedIds.map(id => ({ id: String(id), name: '' })))
    } else if (seg && seg !== 'custom') {
      setSelectedSegments([seg])
      // Segment + hand-picked union — restore the extras too
      if (Array.isArray(pickedIds) && pickedIds.length) {
        setSelectedClients(pickedIds.map(id => ({ id: String(id), name: '' })))
      }
    }
    setStep(1)
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  // Quick-starts from the campaigns hub land straight in the builder with
  // their template applied
  useEffect(() => {
    if (!initialTemplateId) return
    if (!EMAIL_TEMPLATES_V2_VISIBLE) {
      const classic = emailTemplates.find(t => t.id === initialTemplateId)
      if (classic) handleSelectTemplate(classic)
      return
    }
    const tpl = findTemplate(initialTemplateId, market || 'hair')
    if (tpl) applyTemplate(tpl)
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  // From a discount code's Share: an email made for that code, in the
  // salon's brand (header, the code's Offer section, footer)
  useEffect(() => {
    if (!initialOfferCode || initialTemplateId || editCampaign) return
    try {
      const { blocks, subject: codeSubject } = codeEmail(initialOfferCode, { market: market || 'hair', kit: brandKit, links })
      if (blocks.length) handleSelectTemplate({ id: 'code-offer', name: `${initialOfferCode.code} offer`, subject: codeSubject, blocks }, { fromLibrary: true })
    } catch (e) { /* the usual start instead */ }
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  // Fetch audience segments on mount
  useEffect(() => {
    const fetchAudienceSegments = async () => {
      try {
        setLoadingAudience(true)
        const segments = await marketingApiService.getAudienceSegments(dispatch)
        console.log('Fetched audience segments:', segments)
        setAudienceSegments(segments || [])
      } catch (error) {
        console.error('Failed to fetch audience segments:', error)
        // Set empty array on error so UI doesn't break
        setAudienceSegments([])
      } finally {
        setLoadingAudience(false)
      }
    }

    fetchAudienceSegments()
  }, [dispatch])
  
  const selectedBlock = emailBlocks.find(b => b.id === selectedBlockId)

  // ─── Gift-card-style editor helpers ───
  // Panel precedence: image picking > contentFlow > editingBlock > railSection.
  const editingBlock = emailBlocks.find(b => b.id === editingBlockId) || null
  const panelOpen = !!railSection || !!editingBlock || !!contentFlow || showImagePicker
  const closeImagePicking = () => { setShowImagePicker(false); setImagePickerField(null) }
  const closePanel = () => { setRailSection(null); setEditingBlockId(null); setContentFlow(null); closeImagePicking(); setPanelCollapsed(false) }
  const openSection = (id) => { setSelectedBlockId(null); setEditingBlockId(null); setContentFlow(null); closeImagePicking(); setRailSection(prev => (prev === id ? null : id)) }
  const handleCanvasBgClick = () => { setSelectedBlockId(null); setEditingBlockId(null); setRailSection(null); setContentFlow(null); closeImagePicking() }
  // Clicking a block selects it AND opens its editor panel immediately, the
  // way Canva reflects the clicked element in its properties panel. The old
  // select-then-press-Edit flow read as "templates can't be edited".
  const handleBlockSelect = (id) => { setSelectedBlockId(id); setEditingBlockId(id); setRailSection(null); setContentFlow(null); closeImagePicking() }

  // ─── Content flow (type -> style -> item), all inside the panel ───
  const startContentType = (typeMeta) => {
    setContentFlow({ type: typeMeta.type, step: typeMeta.hasStyles ? 'style' : 'pick', style: null, mode: 'insert', targetId: null })
  }
  // Swap an existing block's content, launched from its editor panel. For
  // vouchers this swaps the voucher only; the card design stays and is
  // changed from the design grid inside the block editor.
  const startContentSwap = (type) => {
    if (!editingBlockId) return
    setContentFlow({ type, step: 'pick', style: null, mode: 'swap', targetId: editingBlockId })
  }
  const contentFlowBack = () => {
    setContentFlow(flow => {
      if (!flow) return null
      if (flow.step === 'pick' && flow.mode === 'insert') {
        const meta = contentTypeMeta(flow.type)
        if (meta && meta.hasStyles) return { ...flow, step: 'style', style: null }
      }
      return null // back to type rows (insert) or the block editor (swap)
    })
  }
  const handleContentStylePick = (style) => {
    setContentFlow(flow => (flow ? { ...flow, style, step: 'pick' } : flow))
  }
  // Map a picked item to block content for its block type
  const contentFromItem = (type, item, style) => {
    switch (type) {
      case 'service':
        return { name: item.name, description: item.description, price: String(item.price), duration: item.duration, image: item.image, currency: item.currency, ...(style ? { variant: style } : {}) }
      case 'product':
        return { name: item.name, description: item.description, price: item.price, currency: item.currency, image: item.image, url: item.url, ...(style ? { variant: style } : {}) }
      case 'voucher':
        // Voucher details + its cover design so the block immediately shows
        // the real card face; the design grid in the block editor switches
        // between the voucher's allocated designs afterwards
        return {
          name: item.name, fromPrice: item.fromPrice, value: item.value, promo: item.promo,
          currency: item.currency, expiry: item.expiry, url: item.url, salonName: item.salonName,
          designIds: item.designIds || [], coverDesignId: item.coverDesignId || null,
          ...(item.coverDesign
            ? designToContent(item.coverDesign)
            : { aesthetic: 'editorial', theme: item.theme, template: item.template || 'signature' }),
          ...(style ? { variant: style } : {})
        }
      case 'review':
        return { text: item.text, author: item.author, service: item.service, rating: item.rating }
      case 'team':
        return { name: item.name, title: item.title, bio: item.bio, image: item.image }
      default:
        return {}
    }
  }
  const handleContentItemPick = (item) => {
    if (!contentFlow) return
    const { type, style, mode, targetId } = contentFlow
    if (isV2 && mode !== 'swap') {
      const sec = sectionFromContent(type, item, style)
      if (sec) {
        const [resolved] = resolveLocalLinks([{ type: sec.type, content: sec.content }], links)
        const newId = insertBlock(sec.type, resolved.content, insertionIndex())
        setContentFlow(null)
        setRailSection(null)
        setEditingBlockId(newId)
      }
      return
    }
    if (mode === 'swap' && targetId) {
      // Swap keeps the block's layout; for vouchers it also keeps the chosen
      // card design (only the voucher details + allocation change)
      let data = contentFromItem(type, item, null)
      if (type === 'voucher') {
        const { designId, aesthetic, theme, template, customDesign, variant, ...voucherOnly } = data
        data = voucherOnly
      }
      updateBlock(targetId, data)
      setContentFlow(null)
      return
    }
    const newId = insertBlock(type, contentFromItem(type, item, style), viewportTopIndex())
    setContentFlow(null)
    setRailSection(null)
    setEditingBlockId(newId)
  }

  // ─── Drag-to-reorder state ───
  // Dragging is armed from the grip handle only, so text selection and block
  // clicks never accidentally start a drag. dropIndex is the insertion index.
  const [dragArmedId, setDragArmedId] = useState(null)
  const [dragIndex, setDragIndex] = useState(null)
  const [dropIndex, setDropIndex] = useState(null)

  // Vertical centre of the selected block within the email wrapper, so the
  // action rail can float OUTSIDE the email card (which clips its children
  // with overflow:hidden) while staying level with the block it acts on.
  const [railTop, setRailTop] = useState(null)
  useEffect(() => {
    if (step !== 1 || !selectedBlockId) { setRailTop(null); return undefined }
    const wrap = canvasRef.current && canvasRef.current.querySelector('.eb-email-wrap')
    const el = wrap && wrap.querySelector(`[data-bid="${selectedBlockId}"]`)
    if (!el) { setRailTop(null); return undefined }
    const measure = () => setRailTop(el.offsetTop + el.offsetHeight / 2)
    measure()
    let ro = null
    if (typeof ResizeObserver !== 'undefined') {
      ro = new ResizeObserver(measure)
      ro.observe(el)
      const email = wrap.querySelector('.eb-email')
      if (email) ro.observe(email)
    }
    return () => { if (ro) ro.disconnect() }
  }, [step, selectedBlockId, emailBlocks, previewMode]) // eslint-disable-line react-hooks/exhaustive-deps

  // Mobile: when a block's editor opens, expand the sheet and scroll that
  // block to the top of the visible strip above it. Without this the sheet
  // covers whatever is being edited and every change is made blind.
  useEffect(() => {
    if (!editingBlockId) return undefined
    setPanelCollapsed(false)
    if (!isMobile || step !== 1) return undefined
    const t = setTimeout(() => {
      const el = canvasRef.current && canvasRef.current.querySelector(`[data-bid="${editingBlockId}"]`)
      if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' })
    }, 80) // let the sheet's open transition start so heights are settled
    return () => clearTimeout(t)
  }, [editingBlockId, isMobile, step]) // eslint-disable-line react-hooks/exhaustive-deps

  // The panel body is one long-lived element whose content swaps per block,
  // so it keeps the previous block's scroll position — a freshly opened
  // editor would start half way down. Reset it whenever the content changes.
  useEffect(() => {
    const body = document.querySelector('.eb-panel-body')
    if (body) body.scrollTop = 0
  }, [editingBlockId, railSection, contentFlow, showImagePicker])

  // Same idea one level up: every wizard step shares the CreatorFrame body
  // as its scroller, so without this a new step opened wherever the previous
  // one was scrolled to (e.g. Review starting at the bottom on mobile).
  useEffect(() => {
    const body = document.querySelector('.cf-body')
    if (body) body.scrollTop = 0
    window.scrollTo(0, 0)
  }, [step])

  // Mobile: hide the device toggle on scroll down, bring it back on scroll up.
  useEffect(() => {
    if (!isMobile || step !== 1) return undefined
    const el = canvasRef.current
    if (!el) return undefined
    let last = el.scrollTop
    const onScroll = () => {
      const y = el.scrollTop
      if (Math.abs(y - last) < 8) return // ignore jitter
      setCtxHidden(y > last && y > 48)
      last = y
    }
    el.addEventListener('scroll', onScroll, { passive: true })
    return () => el.removeEventListener('scroll', onScroll)
  }, [isMobile, step])

  const canvasRef = useRef(null)
  const blockEls = () => (canvasRef.current ? Array.from(canvasRef.current.querySelectorAll('.eb-email > *')) : [])
  // Index of the block currently at the top of the canvas viewport, so a clicked
  // insert lands where the salon is looking rather than at the very bottom.
  const viewportTopIndex = () => {
    const canvas = canvasRef.current
    if (!canvas) return null
    const threshold = canvas.getBoundingClientRect().top + 64
    const els = blockEls()
    for (let i = 0; i < els.length; i++) {
      if (els[i].getBoundingClientRect().bottom > threshold) return i
    }
    return null
  }

  // Themed emails: a new section goes after the block in the middle of the
  // view (never above the brand header, which "before the top block" did
  // whenever the email was scrolled to the top).
  const insertionIndex = () => {
    if (!isV2) return viewportTopIndex()
    const canvas = canvasRef.current
    if (!canvas) return null
    const rect = canvas.getBoundingClientRect()
    const mid = rect.top + rect.height / 2
    const els = blockEls()
    for (let i = 0; i < els.length; i++) {
      if (els[i].getBoundingClientRect().bottom >= mid) return i + 1
    }
    return null
  }

  // Insert a block at `atIndex` (defaults to before the footer), then select it
  // and scroll it into view so it's obvious where it landed.
  const insertBlock = (type, overrides = {}, atIndex = null) => {
    const id = generateId()
    let content = { ...getDefaultContent(type), ...overrides }
    // New sections may carry {{GiftCardLink}} / {{ShopLink}}: fill in this salon's
    if (isSectionType(type)) content = resolveLocalLinks([{ type, content }], links)[0].content
    const newBlock = { id, type, content }
    const footerIndex = emailBlocks.findIndex(b => b.type === 'footer')
    let idx = atIndex == null ? (footerIndex >= 0 ? footerIndex : emailBlocks.length) : atIndex
    if (footerIndex >= 0) idx = Math.min(idx, footerIndex)
    idx = Math.max(0, Math.min(idx, emailBlocks.length))
    setEmailBlocks(blocks => [...blocks.slice(0, idx), newBlock, ...blocks.slice(idx)])
    setSelectedBlockId(id)
    setTimeout(() => {
      const els = blockEls()
      if (els[idx]) els[idx].scrollIntoView({ behavior: 'smooth', block: 'center' })
    }, 70)
    return id
  }
  const handleInsertItem = (item) => {
    const id = insertBlock(item.type, item.overrides, insertionIndex())
    if (item.picker === 'image') setShowImagePicker(true)
    else if (item.picker) {
      // A new section's own image field (e.g. 'src'), straight to the picker
      setImagePickerField(item.picker)
      setShowImagePicker(true)
    }
    setRailSection(null)
    if (isSectionType(item.type)) setEditingBlockId(id)
  }
  const handleInsertSection = (type) => {
    const id = insertBlock(type, {}, insertionIndex())
    setRailSection(null)
    setEditingBlockId(id)
    hapticTick()
  }
  const handleInsertLayout = (layout) => {
    const b = layout.blocks && layout.blocks[0]
    if (b) insertBlock(b.type, b.content, viewportTopIndex())
    setRailSection(null)
  }
  // Template selection
  const loadDraft = (draft) => {
    setCampaignName(draft.name)
    setSubject(draft.subject)
    setEmailBlocks((draft.blocks.length ? draft.blocks : BLANK_TEMPLATE.blocks).map(b => ({ ...b, id: generateId() })))
    setEditingCampaignId(draft.id)
    setStep(1)
  }

  const handleSaveAsTemplate = () => {
    const salonId = localStorage.getItem('salonId')
    // The trade it shows under in the library: the template it came from,
    // else the salon's own (changeable from the card's menu)
    const trade = templateTrade({ blocks: emailBlocks }, market || 'all')
    saveUserTemplate(salonId, { name: campaignName || subject || 'Untitled template', subject, blocks: withTemplateTrade(emailBlocks, trade) })
    setUserTemplates(loadUserTemplates(salonId))
    setTplSavedFlash(true)
    setTimeout(() => setTplSavedFlash(false), 2200)
  }

  const handleDeleteUserTemplate = (id) => {
    setUserTemplates(deleteUserTemplate(localStorage.getItem('salonId'), id))
  }
  // "Show it under": move one of the salon's templates to another trade
  const handleSetTemplateTrade = (tpl, trade) => {
    const salonId = localStorage.getItem('salonId')
    saveUserTemplate(salonId, { id: tpl.id, name: tpl.name, subject: tpl.subject, blocks: withTemplateTrade(tpl.blocks, trade) })
    setUserTemplates(loadUserTemplates(salonId))
  }

  const handleSelectTemplate = (template, { fromLibrary = false } = {}) => {
    setSubject(template.subject || '')
    setCampaignName((n) => n || template.name || '')
    const blocks = template.blocks.map(b => ({
      ...b,
      id: generateId()
    }))
    setEmailBlocks(blocks)
    appliedRef.current = fromLibrary ? { blocks } : null
    setStep(1)
  }
  // A library template, in the chosen look and the salon's brand
  const applyTemplate = (tpl, look) => {
    let blocks = buildTemplateBlocks(tpl, { market: market || 'hair', kit: brandKit, look, links })
    // Remember which trade it was made for, should it be saved as a template
    if (['hair', 'barber', 'beauty'].includes(tpl.market)) blocks = withTemplateTrade(blocks, tpl.market)
    handleSelectTemplate({ ...tpl, blocks }, { fromLibrary: true })
  }
  const startBlank = () => {
    applyTemplate(blankTemplate(brandLookOf(brandKit, market)), BRAND_LOOK)
  }
  // The brand saves itself: every change in the Brand panel (and a new
  // logo) becomes the salon's brand for new emails, a moment after the last
  // change so dragging the logo slider doesn't write on every step.
  // Only what was changed is saved, merged into the brand: a colour picked
  // while this email wears another look doesn't wipe the brand's type or
  // buttons. The look itself isn't saved here: the brand's look is set in
  // Marketing > Config, and other looks are for one email.
  const BRAND_EDIT_KEYS = ['accent', 'logo', 'logoWidth', 'logoShape', 'logoShapeFor', 'name', 'display', 'text', 'button', 'radius']
  const brandSaveRef = useRef({ timer: null, flash: null, patch: null })
  const writeBrand = (patch) => {
    const kit = { ...(loadBrandKit(brandSalonId) || {}) }
    Object.keys(patch).forEach((k) => {
      if (patch[k] === undefined || patch[k] === '') delete kit[k]
      else kit[k] = patch[k]
    })
    // A logo taken off stays off, even though Settings still has one
    if ('logo' in patch && !kit.logo && salonKit && salonKit.logo) kit.logo = null
    setSavedKit(saveBrandKit(brandSalonId, kit))
    pushBrandKit(brandSalonId, kit)
  }
  const persistBrand = (patch) => {
    const r = brandSaveRef.current
    clearTimeout(r.timer)
    r.patch = { ...(r.patch || {}), ...patch }
    r.timer = setTimeout(() => {
      const p = r.patch
      r.patch = null
      writeBrand(p)
      setKitSavedFlash(true)
      clearTimeout(r.flash)
      r.flash = setTimeout(() => setKitSavedFlash(false), 2200)
    }, 500)
  }
  // Leaving the builder mid-change still saves it
  useEffect(() => () => {
    const r = brandSaveRef.current
    clearTimeout(r.timer)
    clearTimeout(r.flash)
    if (r.patch) writeBrand(r.patch)
  }, []) // eslint-disable-line react-hooks/exhaustive-deps
  const handleBrandChange = (spec) => {
    const prev = themeSpec || {}
    const patch = {}
    BRAND_EDIT_KEYS.forEach((k) => {
      if (JSON.stringify(prev[k]) !== JSON.stringify(spec && spec[k])) patch[k] = spec ? spec[k] : undefined
    })
    applyThemeSpec(spec)
    if (Object.keys(patch).length) persistBrand(patch)
  }
  // The salon's brand as a whole look, for the Brand panel's first tile
  const brandLook = useMemo(() => ({
    ...brandKitSpec(brandKit),
    look: brandLookOf(brandKit, market),
  }), [brandKit, market])
  // This email in the salon's brand, content untouched
  const applyBrandLook = () => {
    hapticTick()
    const next = { ...(themeSpec || {}) }
    ;[...BRAND_EDIT_KEYS, 'look'].forEach((k) => { delete next[k] })
    applyThemeSpec({ ...next, ...brandLook })
  }
  // Another look for this email only: its own colour, type and buttons, with
  // the salon's logo and name kept
  const pickLookForEmail = (id) => {
    hapticTick()
    const cur = themeSpec || {}
    const next = { ...cur, look: id }
    ;['accent', 'display', 'text', 'button', 'radius'].forEach((k) => { delete next[k] })
    applyThemeSpec(next)
  }
  
  // Block operations
  const updateBlock = (blockId, updates) => {
    setEmailBlocks(blocks => blocks.map(block => 
      block.id === blockId 
        ? { ...block, content: { ...block.content, ...updates } }
        : block
    ))
  }
  
  const deleteBlock = (blockId) => {
    setEmailBlocks(blocks => blocks.filter(b => b.id !== blockId))
    if (selectedBlockId === blockId) {
      setSelectedBlockId(null)
    }
  }
  
  const duplicateBlock = (blockId) => {
    const block = emailBlocks.find(b => b.id === blockId)
    if (block) {
      const newBlock = {
        ...block,
        id: generateId(),
        content: { ...block.content }
      }
      const index = emailBlocks.findIndex(b => b.id === blockId)
      setEmailBlocks(blocks => [
        ...blocks.slice(0, index + 1),
        newBlock,
        ...blocks.slice(index + 1)
      ])
      setSelectedBlockId(newBlock.id)
    }
  }
  
  const moveBlock = (blockId, direction) => {
    setEmailBlocks(blocks => {
      const index = blocks.findIndex(b => b.id === blockId)
      if (direction === 'up' && index > 0) {
        const newBlocks = [...blocks]
        ;[newBlocks[index], newBlocks[index - 1]] = [newBlocks[index - 1], newBlocks[index]]
        return newBlocks
      }
      if (direction === 'down' && index < blocks.length - 1) {
        const newBlocks = [...blocks]
        ;[newBlocks[index], newBlocks[index + 1]] = [newBlocks[index + 1], newBlocks[index]]
        return newBlocks
      }
      return blocks
    })
  }

  // Move a block from one index to an insertion index (drag-and-drop reorder).
  // The footer stays pinned to the bottom.
  const reorderBlocks = (from, to) => {
    setEmailBlocks(blocks => {
      if (from == null || to == null || from === to || from === to - 1) return blocks
      const footerIndex = blocks.findIndex(b => b.type === 'footer')
      let insertAt = to
      if (footerIndex >= 0 && blocks[from].type !== 'footer') insertAt = Math.min(insertAt, footerIndex)
      const next = [...blocks]
      const [moved] = next.splice(from, 1)
      next.splice(insertAt > from ? insertAt - 1 : insertAt, 0, moved)
      return next
    })
  }

  const handleBlockDragStart = (e, index) => {
    setDragIndex(index)
    e.dataTransfer.effectAllowed = 'move'
    try { e.dataTransfer.setData('text/plain', String(index)) } catch (err) { /* IE/edge cases */ }
  }
  const handleBlockDragOver = (e, index) => {
    if (dragIndex == null) return
    e.preventDefault()
    e.dataTransfer.dropEffect = 'move'
    const rect = e.currentTarget.getBoundingClientRect()
    const before = e.clientY < rect.top + rect.height / 2
    setDropIndex(before ? index : index + 1)
  }
  const handleBlockDrop = (e) => {
    e.preventDefault()
    if (dragIndex != null && dropIndex != null) {
      const movedId = emailBlocks[dragIndex] && emailBlocks[dragIndex].id
      reorderBlocks(dragIndex, dropIndex)
      if (movedId) setSelectedBlockId(movedId)
    }
    setDragIndex(null); setDropIndex(null); setDragArmedId(null)
  }
  const handleBlockDragEnd = () => { setDragIndex(null); setDropIndex(null); setDragArmedId(null) }

  // ─── Keyboard: undo / redo / escape / delete while building ───
  useEffect(() => {
    if (step !== 1) return undefined
    const onKeyDown = (e) => {
      // While a modal is up, leave the keyboard to it.
      if (showScheduleModal) return
      const inField = e.target && (e.target.closest ? e.target.closest('input, textarea, select, [contenteditable="true"]') : null)
      const mod = e.metaKey || e.ctrlKey
      if (mod && !inField && (e.key === 'z' || e.key === 'Z')) {
        e.preventDefault()
        if (e.shiftKey) redo(); else undo()
        return
      }
      if (mod && !inField && (e.key === 'y' || e.key === 'Y')) {
        e.preventDefault(); redo(); return
      }
      if (e.key === 'Escape') {
        if (showImagePicker) closeImagePicking()
        else if (contentFlow) contentFlowBack()
        else if (railSection || editingBlockId) { setRailSection(null); setEditingBlockId(null) }
        else if (selectedBlockId) setSelectedBlockId(null)
        return
      }
      if (!inField && (e.key === 'Backspace' || e.key === 'Delete') && selectedBlockId) {
        const block = emailBlocks.find(b => b.id === selectedBlockId)
        if (block && block.type !== 'footer') {
          e.preventDefault()
          deleteBlock(selectedBlockId)
          setEditingBlockId(null)
        }
      }
    }
    window.addEventListener('keydown', onKeyDown)
    return () => window.removeEventListener('keydown', onKeyDown)
  }, [step, selectedBlockId, editingBlockId, railSection, contentFlow, emailBlocks, showImagePicker, showScheduleModal]) // eslint-disable-line react-hooks/exhaustive-deps


  // Default content for new blocks
  const getDefaultContent = (type) => {
    if (isSectionType(type)) return sectionDefaults(type)
    const defaults = {
      text: { text: '', align: 'left', fontSize: '16', textColor: '#374151' },
      button: { text: 'Book Now', url: '{{BookingLink}}', buttonColor: '#29282D', textColor: '#ffffff', align: 'center' },
      image: { url: '', alt: '' },
      logo: { url: '', maxWidth: '150', align: 'center' },
      divider: { color: '#e5e5e5' },
      spacer: { height: '30' },
      columns: { leftContent: [], rightContent: [], gap: '20' },
      social: { style: 'rounded' },
      review: { text: '', author: '', service: '', rating: 5, backgroundColor: '#f9fafb' },
      service: { name: '', description: '', price: '', duration: '', priceColor: '#007EA0', backgroundColor: '#fafafa' },
      product: { name: '', description: '', price: '', currency: '£', image: '', url: '', buttonText: 'Shop now', priceColor: '#1a1a1a', buttonColor: '#29282D', buttonTextColor: '#ffffff', backgroundColor: '#fafafa' },
      voucher: { name: 'Gift Voucher', variant: 'showcase', template: 'signature', theme: 'rose', aesthetic: 'editorial', value: '50', fromPrice: '25', promo: '', currency: '£', expiry: '', url: '', buttonText: 'Buy gift voucher', salonName: 'Your Salon' },
      team: { name: '', title: '', bio: '' },
      footer: { text: '© 2026 {{SalonName}}. All rights reserved.', backgroundColor: '#f9fafb', textColor: '#9ca3af' },
      // Layout defaults mirror the palette entries in blockLayouts.jsx so the
      // same block type starts identically from either insert path
      'layout-gradient': { headline: 'Big Announcement', subtext: 'Something exciting is here', gradient: 'purple', textColor: '#ffffff' },
      'layout-arch': { headline: 'Welcome', subtext: 'We\'re glad you\'re here', imageUrl: '', textColor: '#ffffff' },
      'layout-cta-gradient': { headline: 'Ready to Book?', buttonText: 'Book Now', buttonUrl: '{{BookingLink}}', background: 'linear-gradient(135deg, #007EA0, #059669)' },
      'layout-testimonial': { quote: 'Amazing experience!', name: 'Client Name', title: 'Regular Client', avatarUrl: '' },
      'layout-stats': { stats: [{ value: '500+', label: 'Happy Clients' }, { value: '15', label: 'Years' }, { value: '4.9', label: 'Rating' }] },
      'layout-social-strip': { facebook: '', instagram: '', twitter: '', tiktok: '', style: 'rounded' },
      'layout-fancy-divider': { color: '#007EA0' },
      'layout-before-after': { beforeImage: '', afterImage: '', beforeLabel: 'Before', afterLabel: 'After' },
      'layout-promo-banner': { promoText: '20% OFF', subtext: 'Use code SAVE20', backgroundColor: '#fbbf24' },
      'layout-price-card': { serviceName: 'Service Name', description: 'Service description', originalPrice: '129', salePrice: '99' },
      'layout-full-bleed': { headline: 'Bold Statement', subtext: 'Supporting message', imageUrl: '', overlayOpacity: 40 },
      'layout-hero-split': { headline: 'Feature Title', body: 'Description text', imageUrl: '', imagePosition: 'left' },
      'layout-bento-2col': {
        mainImage: 'https://images.unsplash.com/photo-1560066984-138dadb4c035?w=600',
        topImage: 'https://images.unsplash.com/photo-1522337360788-8b13dee7a37e?w=400',
        bottomImage: 'https://images.unsplash.com/photo-1487412947147-5cebf100ffc2?w=400',
        mainText: ''
      },
      'layout-bento-3col': { images: [
        'https://images.unsplash.com/photo-1521590832167-7bcbfaa6381f?w=400',
        'https://images.unsplash.com/photo-1522337094846-8a818192de1f?w=400',
        'https://images.unsplash.com/photo-1570172619644-dfd03ed5d881?w=400',
        'https://images.unsplash.com/photo-1580618672591-eb180b1a973f?w=400'
      ] },
      'layout-feature-card': { headline: 'Feature Title', body: 'Description text', imageUrl: '', cornerRadius: 16 },
      'layout-glass-card': { headline: 'Elegant Title', body: 'Description', gradient: 'purple' },
      'layout-rounded-image': { imageUrl: '', cornerRadius: 24 },
      'layout-badge': { badgeText: 'NEW', headline: 'Something exciting', body: 'A short supporting line to build the moment.' },
      'layout-countdown': { headline: 'Offer Ends In', days: '02', hours: '14', mins: '30', secs: '00', backgroundColor: '#1a1a1a' },
      'layout-editorial-hero': { eyebrow: 'A note from us', headline: 'A headline that sets the tone', subtext: 'A line of supporting copy to set the scene.', backgroundColor: '#F5EDE3', textColor: '#2C3E50', accentColor: '#C17F59', headlineFontFamily: 'Georgia, serif', headlineSize: 44 },
      'layout-utility-bar': { leftText: 'Your salon', rightText: 'Est. 2026', textColor: '#1a1a1a', backgroundColor: '#ffffff', showRule: true },
      'layout-section-intro': { eyebrow: 'The details', heading: 'A heading for this section', body: 'A short introduction for the content that follows.', textColor: '#1a1a1a', accentColor: '#9ca3af' },
      'layout-editorial': { headline: 'Feature Story', body: 'Add your compelling story here...', imageUrl: '', textColor: '#1a1a1a' },
      'layout-polaroid': { caption: 'Your caption here', imageUrl: '', backgroundColor: '#ffffff' },
      'layout-duo-grid': { leftImage: '', leftTitle: 'First story', leftCaption: 'A short supporting line.', rightImage: '', rightTitle: 'Second story', rightCaption: 'A short supporting line.', backgroundColor: '#F5EDE3', textColor: '#2C3E50' },
      'layout-shape-image': { shape: 'arch', imageUrl: '', frameColor: '', width: 78, backgroundColor: '#F5EDE3' },
      'layout-quote-chips': { q1: '"so good"', q2: '"love this place"', q3: '"my happy hour"', q4: '"booked again already"', chipColor: '#ffffff', textColor: '#1a1a1a', backgroundColor: '#F5EDE3' },
      'layout-contact-card': { address: '', phone: '', hours: '', email: '' },
      'layout-wave-divider': { color: '#F5EDE3', position: 'bottom' }
    }
    return defaults[type] || {}
  }
  
  // Audience operations. Segments are single-select: the API only accepts
  // one audienceSegment, so multi-select UI silently dropped extras before.
  const toggleSegment = (segmentId) => {
    setSelectedSegments(prev => (prev[0] === segmentId ? [] : [segmentId]))
  }

  const getTotalAudienceSize = () => {
    // Segment total + hand-picked extras (overlap dedupes at send time, so
    // this is an upper-bound estimate)
    return selectedSegments.reduce((sum, id) => {
      const segment = audienceSegments.find(s => s.id === id)
      return sum + (segment?.count || 0)
    }, 0) + selectedClients.length
  }

  // Human-readable audience for the review step
  const audienceLabel = () => {
    const segment = audienceSegments.find(s => s.id === selectedSegments[0])
    if (!segment) {
      if (!selectedClients.length) return 'No one selected'
      return selectedClients.length === 1 ? selectedClients[0].name : `${selectedClients.length} hand-picked clients`
    }
    return selectedClients.length
      ? `${segment.name} + ${selectedClients.length} hand-picked`
      : segment.name
  }

  
  // Handle image selection
  const handleImageSelect = (url) => {
    if (imagePickerField === 'brand.logo') {
      handleBrandChange({ ...(themeSpec || {}), logo: url, logoShape: undefined, logoShapeFor: undefined })
    } else if (selectedBlock) {
      if (imagePickerField && imagePickerField.includes('.')) {
        // A picture inside a list item, e.g. items.1.image
        const [listKey, index, key] = imagePickerField.split('.')
        const list = Array.isArray(selectedBlock.content && selectedBlock.content[listKey]) ? selectedBlock.content[listKey] : []
        updateBlock(selectedBlock.id, { [listKey]: list.map((it, i) => (i === Number(index) ? { ...it, [key]: url } : it)) })
      } else if (imagePickerField) {
        updateBlock(selectedBlock.id, { [imagePickerField]: url })
      } else if (selectedBlock.type === 'image') {
        updateBlock(selectedBlock.id, { url })
      } else if (selectedBlock.type === 'logo') {
        updateBlock(selectedBlock.id, { url })
      } else if (selectedBlock.type === 'layout-testimonial') {
        updateBlock(selectedBlock.id, { avatarUrl: url })
      } else if (selectedBlock.type.startsWith('layout-')) {
        updateBlock(selectedBlock.id, { imageUrl: url })
      } else {
        updateBlock(selectedBlock.id, { image: url })
      }
    }
    setShowImagePicker(false)
    setImagePickerField(null)
  }
  
  
  // The finished email for the sending engine: the same HTML the builder
  // shows, merge tags intact. Only themed emails have it; classic emails are
  // still rendered by the server from emailBlocks.
  const emailHtmlPayload = () => buildEmailHtmlPayload(emailBlocks, links, subject)

  // After the send HERE has succeeded: the same campaign at the other chosen
  // locations. Never throws — a failure there doesn't undo the send here; the
  // results sheet says what happened and can try again (the server is
  // idempotent per location). Returns false when there's nothing to do, so
  // the caller finishes exactly as it always has.
  const chosenOtherSites = () => (sendSites.show ? otherSiteIds.filter(id => sendSites.others.some(s => String(s.salonId) === String(id))) : [])
  const runSiteFan = async (fan) => {
    try {
      const results = await broadcastToSites(fan.campaignId, fan.ids, { sendNow: fan.sendNow })
      setSiteFan({ ...fan, results, error: null, retrying: false })
    } catch (e) {
      setSiteFan({ ...fan, results: [], error: (e && e.message) || 'We couldn’t reach your other locations.', retrying: false })
    }
  }
  const fanOutToSites = async (campaignId, sendNow, after) => {
    const ids = chosenOtherSites()
    if (!ids.length) return false
    await runSiteFan({ campaignId, sendNow, ids, after })
    return true
  }

  // Send campaign
  const handleSend = async () => {
    if (isSending) return

    setIsSending(true)
    setSendError(null)

    try {
      // First create the campaign
      const campaignData = {
        name: campaignName || subject || 'Untitled Campaign',
        type: 'email',
        subject: subject,
        emailBlocks: emailBlocks,
        ...emailHtmlPayload(),
        ...senderIdentityPayload(),
        audienceSegment: selectedSegments[0] || 'custom',
        // Hand-picked clients ride along in EITHER mode — segment + picks union
        clientIds: selectedClients.map(c => c.id),
        sendNow: true
      }

      const createdCampaign = await persistCampaign(campaignData)

      if (createdCampaign && createdCampaign._id) {
        // Send the campaign immediately
        await marketingApiService.sendCampaign(dispatch, {
          campaignId: createdCampaign._id,
          sendNow: true
        })

        const finish = () => {
          setStep(4)
          onSave && onSave({ subject, emailBlocks, selectedSegments }, 'sent')
        }
        if (!(await fanOutToSites(createdCampaign._id, true, finish))) finish()
      }
    } catch (error) {
      console.error('Error sending campaign:', error)
      setSendError(error.message || 'Something went wrong while sending. Please try again.')
    } finally {
      setIsSending(false)
    }
  }

  // Handle scheduling
  const handleSchedule = async (scheduledDate) => {
    if (isSending) return

    setShowScheduleModal(false)
    setIsSending(true)
    setSendError(null)

    try {
      // Create scheduled campaign
      const campaignData = {
        name: campaignName || subject || 'Untitled Campaign',
        type: 'email',
        subject: subject,
        emailBlocks: emailBlocks,
        ...emailHtmlPayload(),
        ...senderIdentityPayload(),
        audienceSegment: selectedSegments[0] || 'custom',
        // Hand-picked clients ride along in EITHER mode — segment + picks union
        clientIds: selectedClients.map(c => c.id),
        scheduledDate: scheduledDate.date,
        scheduledTime: scheduledDate.time
      }

      const createdCampaign = await persistCampaign(campaignData)

      // persistCampaign only saves the campaign (BE defaults it to draft); tell
      // the BE to mark it scheduled so the campaigns-send cron will dispatch it.
      if (createdCampaign && createdCampaign._id) {
        await marketingApiService.sendCampaign(dispatch, {
          campaignId: createdCampaign._id,
          sendNow: false
        })
      }

      setIsScheduled(true)
      const finish = () => {
        setStep(4)
        onSave && onSave({ subject, emailBlocks, selectedSegments, scheduledDate }, 'scheduled')
      }
      // Each copy is scheduled for the same date and time it carries from this one.
      if (!(createdCampaign && createdCampaign._id && await fanOutToSites(createdCampaign._id, false, finish))) finish()
    } catch (error) {
      console.error('Error scheduling campaign:', error)
      setSendError(error.message || 'Something went wrong while scheduling. Please try again.')
    } finally {
      setIsSending(false)
    }
  }
  
  // Create or, when resuming a draft, update the existing campaign
  const persistCampaign = async (payload) => {
    if (editingCampaignId) {
      await marketingApiService.updateCampaign(dispatch, { campaignId: editingCampaignId, ...payload })
      return { _id: editingCampaignId }
    }
    return marketingApiService.createCampaign(dispatch, payload)
  }

  // Exit the whole creator (X / Cancel in the frame top bar)
  const handleExit = () => { if (onBack) onBack() }

  // Save the campaign as a draft (frame's Save button)
  const handleSaveDraft = async () => {
    if (savingDraft) return
    setSavingDraft(true)
    setSendError(null)
    try {
      await persistCampaign({
        name: campaignName || subject || 'Untitled campaign',
        type: 'email',
        subject,
        emailBlocks,
        ...emailHtmlPayload(),
        ...senderIdentityPayload(),
        audienceSegment: selectedSegments[0] || 'custom',
        // Hand-picked clients ride along in EITHER mode — segment + picks union
        clientIds: selectedClients.map(c => c.id),
        status: 'draft',
      })
      onSave && onSave({ subject, emailBlocks, campaignName }, 'draft')
    } catch (error) {
      console.error('Error saving draft:', error)
      setSendError(error.message || 'Could not save the draft. Please try again.')
      setSavingDraft(false)
    }
  }

  // Keyboard: undo / redo (skip while typing in a field)
  useEffect(() => {
    const onKey = (e) => {
      const mod = e.metaKey || e.ctrlKey
      if (!mod) return
      const tag = (e.target?.tagName || '').toLowerCase()
      if (tag === 'input' || tag === 'textarea' || e.target?.isContentEditable) return
      const k = e.key.toLowerCase()
      if (k === 'z') { e.preventDefault(); if (e.shiftKey) redo(); else undo() }
      else if (k === 'y') { e.preventDefault(); redo() }
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [undo, redo])

  // Success Screen
  if (step === 4) {
    return (
      <div style={{
        minHeight: '100vh',
        backgroundColor: '#fafafa',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        position: 'relative',
        overflow: 'hidden'
      }}>
        {/* Confetti */}
        <div style={{ position: 'absolute', inset: 0, pointerEvents: 'none' }}>
          {[...Array(50)].map((_, i) => (
            <div
              key={i}
              style={{
                position: 'absolute',
                width: `${Math.random() * 10 + 5}px`,
                height: `${Math.random() * 10 + 5}px`,
                backgroundColor: ['#29282D', '#007EA0', '#f59e0b', '#dc2626', '#059669', '#3b82f6', '#8b5cf6'][Math.floor(Math.random() * 7)],
                left: `${Math.random() * 100}%`,
                top: `-${Math.random() * 20}px`,
                opacity: Math.random() * 0.7 + 0.3,
                borderRadius: Math.random() > 0.5 ? '50%' : '2px',
                animation: `fall ${Math.random() * 3 + 2}s linear infinite`,
                animationDelay: `${Math.random() * 2}s`
              }}
            />
          ))}
        </div>

        {/* Content */}
        <div style={{ textAlign: 'center', maxWidth: '400px', position: 'relative', zIndex: 1 }}>
          <div style={{
            width: '100px',
            height: '100px',
            borderRadius: '50%',
            backgroundColor: '#29282D',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            margin: '0 auto 24px',
            boxShadow: '0 10px 40px rgba(41, 40, 45, 0.3)',
            animation: 'popIn 0.4s ease-out'
          }}>
            <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.5">
              <polyline points="20 6 9 17 4 12"/>
            </svg>
          </div>
          <h1 style={{ fontSize: '28px', fontWeight: '700', color: '#1a1a1a', margin: '0 0 12px 0' }}>
            {isScheduled ? 'Campaign Scheduled!' : 'Campaign Sent!'}
          </h1>
          <p style={{ fontSize: '16px', color: '#6b7280', margin: '0 0 32px 0' }}>
            Your campaign {isScheduled ? 'will be sent' : 'is on its way'} to {getTotalAudienceSize().toLocaleString()} clients.
          </p>
          <button
            onClick={onBack}
            style={{
              padding: '14px 28px',
              backgroundColor: '#29282D',
              color: 'white',
              border: 'none',
              borderRadius: '8px',
              fontSize: '15px',
              fontWeight: '600',
              cursor: 'pointer'
            }}
          >
            Back to Campaigns
          </button>
        </div>
        <style>{`
          @keyframes popIn {
            0% { transform: scale(0); opacity: 0; }
            70% { transform: scale(1.1); }
            100% { transform: scale(1); opacity: 1; }
          }
          @keyframes fall {
            0% { transform: translateY(0) rotate(0deg); }
            100% { transform: translateY(100vh) rotate(360deg); }
          }
        `}</style>
      </div>
    )
  }
  
  // The forward action lives in the frame top bar (next to Save draft).
  const requireBusinessDetails = (proceed) => {
    if (isBusinessDetailsComplete(loadBusinessDetails(localStorage.getItem('salonId')))) { proceed(); return }
    setBizDraft(loadBusinessDetails(localStorage.getItem('salonId')) || { legalForm: '', registeredName: '', companyNumber: '', placeOfRegistration: 'England and Wales', registeredOffice: '', tradingName: '' })
    setAfterBizGate(() => proceed)
    setShowBizGate(true)
  }

  const stepPrimary =
    step === 1 ? (isDesignMode
      ? { label: 'Save design', onClick: () => { if (onSaveDesign) onSaveDesign(designSavePayload({ blocks: emailBlocks, subject, name: campaignName, links })) }, disabled: emailBlocks.length === 0 }
      : { label: 'Continue to audience', onClick: () => setStep(2), disabled: emailBlocks.length === 0 }) :
    step === 2 ? { label: 'Continue to review', onClick: () => setStep(3), disabled: selectedSegments.length === 0 && selectedClients.length === 0 } :
    // Review: on mobile the CTAs live in a pinned bottom bar instead
    step === 3 ? (isMobileView ? null : { label: isSending ? 'Sending…' : 'Send now', onClick: () => requireBusinessDetails(() => setShowSendConfirm(true)), disabled: isSending }) :
    null
  const stepHeaderExtra = step === 1 ? (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: '10px' }}>
      {tplSavedFlash && <span style={{ fontSize: '12.5px', fontWeight: 600, color: '#007EA0' }}>Saved to My Templates</span>}
      <button type="button" className="cf-btn-secondary" onClick={handleSaveAsTemplate} disabled={emailBlocks.length === 0} style={{ height: 40, padding: '0 16px' }}>
        Save as template
      </button>
    </span>
  ) : step === 3 ? (
    <button type="button" className="cf-btn-secondary" onClick={() => requireBusinessDetails(() => setShowScheduleModal(true))} disabled={isSending} style={{ height: 40, padding: '0 18px' }}>Schedule</button>
  ) : null

  return (
    <CreatorFrame
      name={campaignName}
      onNameChange={setCampaignName}
      namePlaceholder={isDesignMode ? 'Untitled template' : 'Untitled campaign'}
      onClose={handleExit}
      onSave={isDesignMode ? undefined : handleSaveDraft}
      saveLabel="Save draft"
      saveVariant="secondary"
      saving={savingDraft}
      canUndo={canUndo}
      canRedo={canRedo}
      onUndo={undo}
      onRedo={redo}
      error={sendError}
      headerExtra={stepHeaderExtra}
      primaryAction={stepPrimary}
    >
      <div style={{ fontFamily: 'inherit', minHeight: '100%', backgroundColor: '#fafafa' }}>
      {/* Step navigation — completed steps are clickable so nobody gets
          stranded on Audience or Review */}
      {step < 4 && (
        <nav className="eb-stepper" aria-label="Campaign steps">
          {(isDesignMode ? [
            { id: 0, label: 'Template' },
            { id: 1, label: 'Design' },
          ] : [
            { id: 0, label: 'Template' },
            { id: 1, label: 'Design' },
            { id: 2, label: 'Audience' },
            { id: 3, label: 'Review' },
          ]).map((s, i) => {
            const isCurrent = step === s.id
            const isDone = step > s.id
            return (
              <React.Fragment key={s.id}>
                {i > 0 && <span className={`eb-stepper-sep ${isDone || isCurrent ? 'is-done' : ''}`} aria-hidden />}
                <button
                  type="button"
                  className={`eb-stepper-item ${isCurrent ? 'is-current' : ''} ${isDone ? 'is-done' : ''}`}
                  onClick={() => { if (isDone) setStep(s.id) }}
                  disabled={!isDone && !isCurrent}
                  aria-current={isCurrent ? 'step' : undefined}
                >
                  <span className="eb-stepper-dot">
                    {isDone ? (
                      <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3.2" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>
                    ) : (
                      i + 1
                    )}
                  </span>
                  {s.label}
                </button>
              </React.Fragment>
            )
          })}
        </nav>
      )}
      
      {/* Step 0: Template library (classic picker where the new one is off) */}
      {step === 0 && !EMAIL_TEMPLATES_V2_VISIBLE && (
        <ClassicTemplatePicker
          isMobile={isMobile}
          isDesignMode={isDesignMode}
          userTemplates={userTemplates}
          drafts={drafts}
          previewMode={previewMode}
          setPreviewMode={setPreviewMode}
          handleSelectTemplate={handleSelectTemplate}
          loadDraft={loadDraft}
          handleDeleteUserTemplate={handleDeleteUserTemplate}
          BLANK_TEMPLATE={BLANK_TEMPLATE}
        />
      )}
      {step === 0 && EMAIL_TEMPLATES_V2_VISIBLE && (
        <TemplatePicker
          market={market}
          kit={brandKit}
          links={links}
          sample={previewSample}
          isMobile={isMobile}
          isDesignMode={isDesignMode}
          userTemplates={userTemplates}
          drafts={drafts}
          onDeleteUserTemplate={handleDeleteUserTemplate}
          onSetTemplateTrade={handleSetTemplateTrade}
          onOpenSaved={(t) => handleSelectTemplate(t)}
          onOpenDraft={loadDraft}
          onUseTemplate={(tpl, look) => { hapticSuccess(); applyTemplate(tpl, look) }}
          onBlank={startBlank}
        />
      )}

      {/* Step 1: Email Builder */}
      {step === 1 && (
        <>
        <div className="eb">
          {/* Left rail */}
          <nav className="eb-rail" aria-label="Insert content">
            {(isV2 ? RAIL_V2 : RAIL).map((s) => {
              const Icon = s.Icon
              return (
                <button
                  key={s.id}
                  type="button"
                  className={`eb-rail-btn ${railSection === s.id ? 'is-active' : ''}`}
                  onClick={() => openSection(s.id)}
                  aria-pressed={railSection === s.id}
                >
                  <span className="eb-rail-icon"><Icon /></span>
                  <span className="eb-rail-label">{s.label}</span>
                </button>
              )
            })}
          </nav>

          {/* Slide-out panel: insert options, or the selected block's editor */}
          <aside className={`eb-panel ${panelOpen ? 'is-open' : ''}${panelCollapsed ? ' is-collapsed' : ''}`}>
            {panelOpen && (
              <>
                <div className="eb-panel-head" onClick={() => { if (panelCollapsed) setPanelCollapsed(false) }}>
                  <div style={{ display: 'flex', alignItems: 'center', minWidth: 0 }}>
                    {(showImagePicker || contentFlow) && (
                      <button type="button" className="eb-panel-back" onClick={showImagePicker ? closeImagePicking : contentFlowBack} aria-label="Back">
                        <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M15 18l-6-6 6-6" /></svg>
                      </button>
                    )}
                    <h3 className="eb-panel-title">
                      {showImagePicker ? (imagePickerField === 'brand.logo' ? 'Choose your logo' : 'Choose an image') : contentFlow ? contentFlowTitle(contentFlow) : (editingBlock ? blockLabel(editingBlock.type) : (isV2 ? SECTION_LABELS_V2 : SECTION_LABELS)[railSection])}
                    </h3>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                    {/* Mobile only (hidden by CSS on desktop): drop the sheet to
                        its header so the whole email is visible, tap to return. */}
                    <button
                      type="button"
                      className="eb-panel-peek"
                      onClick={(e) => { e.stopPropagation(); setPanelCollapsed((c) => !c) }}
                      aria-label={panelCollapsed ? 'Show editing panel' : 'Hide panel to see the email'}
                      aria-expanded={!panelCollapsed}
                    >
                      <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ transform: panelCollapsed ? 'rotate(180deg)' : 'none', transition: 'transform 0.2s' }}><path d="M6 9l6 6 6-6" /></svg>
                    </button>
                    <button type="button" className="eb-panel-close" onClick={closePanel} aria-label="Close panel">
                      <svg width="13" height="13" viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"><path d="M3 3l8 8M11 3l-8 8" /></svg>
                    </button>
                  </div>
                </div>
                <div className={`eb-panel-body ${editingBlock && !contentFlow && !showImagePicker ? 'eb-panel-body--editor' : ''}`}>
                  {showImagePicker ? (
                    <ImagePanel onSelect={handleImageSelect} salon={salonRecord} />
                  ) : contentFlow ? (
                    <ContentPanel
                      flow={contentFlow}
                      onStartType={startContentType}
                      onPickStyle={handleContentStylePick}
                      onPickItem={handleContentItemPick}
                    />
                  ) : editingBlock && isV2 && (isSectionType(editingBlock.type) || editingBlock.type === 'footer') ? (
                    <SectionEditor
                      block={editingBlock}
                      theme={emailTheme}
                      links={links}
                      onUpdate={updateBlock}
                      onDelete={(id) => { deleteBlock(id); closePanel() }}
                      onDuplicate={duplicateBlock}
                      onOpenImagePicker={(path) => {
                        setSelectedBlockId(editingBlock.id)
                        setImagePickerField(path)
                        setShowImagePicker(true)
                      }}
                      onOpenBrand={() => openSection('brand')}
                    />
                  ) : editingBlock ? (
                    <BlockEditor
                      block={editingBlock}
                      onUpdate={updateBlock}
                      onDelete={(id) => { deleteBlock(id); closePanel() }}
                      onDuplicate={duplicateBlock}
                      onOpenImagePicker={(field) => {
                        setImagePickerField(typeof field === 'string' && field !== 'block' ? field : null)
                        setShowImagePicker(true)
                      }}
                      onOpenContentPicker={startContentSwap}
                    />
                  ) : railSection === 'content' ? (
                    <ContentPanel
                      flow={null}
                      onStartType={startContentType}
                      onPickStyle={handleContentStylePick}
                      onPickItem={handleContentItemPick}
                    />
                  ) : railSection === 'brand' ? (
                    isV2 ? (
                      <BrandPanel
                        spec={themeSpec}
                        salonKit={salonKit}
                        onChange={handleBrandChange}
                        onPickLogo={() => { setImagePickerField('brand.logo'); setShowImagePicker(true) }}
                        kitSaved={kitSavedFlash}
                        brand={brandLook}
                        onUseBrand={applyBrandLook}
                        onPickLook={pickLookForEmail}
                      />
                    ) : (
                      <div className="bp" style={{ paddingTop: 12 }}>
                        <div className="bp-notice">
                          <strong>This email uses the classic designer</strong>
                          Looks, brand colours and fonts work on emails started from the new templates. Pick one from Template to try it.
                        </div>
                      </div>
                    )
                  ) : railSection === 'layouts' && isV2 ? (
                    SECTION_GROUPS.map((g) => {
                      const types = Object.keys(SECTIONS).filter(t => SECTIONS[t].group === g.id)
                      if (!types.length) return null
                      return (
                        <div key={g.id} className="eb-insert-section">
                          <p className="eb-insert-section-label">{g.label}</p>
                          <div className="eb-sec-list">
                            {types.map((type) => (
                              <button key={type} type="button" className="eb-sec-tile" onClick={() => handleInsertSection(type)}>
                                <SectionThumb type={type} theme={emailTheme} market={market || 'hair'} />
                                <span className="eb-sec-meta">
                                  <span className="eb-sec-name">{SECTIONS[type].label}</span>
                                  <span className="eb-sec-desc">{SECTIONS[type].description}</span>
                                </span>
                              </button>
                            ))}
                          </div>
                        </div>
                      )
                    })
                  ) : railSection === 'layouts' ? (
                    ['hero', 'gallery', 'content', 'cta', 'image', 'divider', 'social'].map((cat) => {
                      const catLayouts = basicLayouts.filter((l) => l.category === cat)
                      if (!catLayouts.length) return null
                      const labels = { hero: 'Heroes', gallery: 'Gallery', content: 'Content', cta: 'Call to action', image: 'Images', divider: 'Dividers', social: 'Social' }
                      return (
                        <div key={cat} className="eb-insert-section">
                          <p className="eb-insert-section-label">{labels[cat]}</p>
                          <div className="eb-layout-grid">
                            {catLayouts.map((layout) => (
                              <button key={layout.id} type="button" className="eb-layout-tile" onClick={() => handleInsertLayout(layout)}>
                                <div className="eb-layout-thumb">{layout.preview}</div>
                                <div className="eb-layout-name">{layout.name}</div>
                              </button>
                            ))}
                          </div>
                        </div>
                      )
                    })
                  ) : railSection ? (
                    ((isV2 ? INSERT_ITEMS_V2 : INSERT_ITEMS)[railSection] || []).map((item) => (
                      <button key={item.label} type="button" className="eb-insert-row" onClick={() => handleInsertItem(item)}>
                        <span className="eb-insert-icon">
                          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M12 5v14M5 12h14" /></svg>
                        </span>
                        <span className="eb-insert-text">
                          <span className="eb-insert-title">{item.label}</span>
                          <span className="eb-insert-desc">{item.desc}</span>
                        </span>
                      </button>
                    ))
                  ) : null}
                </div>
              </>
            )}
          </aside>

          {/* Canvas */}
          <div className="eb-canvas" ref={canvasRef} onClick={handleCanvasBgClick} style={emailTheme ? { '--eb-fill': emailTheme.palette.canvas } : undefined}>
            <div className={`eb-ctxbar${ctxHidden ? ' is-hidden' : ''}`}>
              <div className="eb-device" onClick={(e) => e.stopPropagation()}>
                <button className={previewMode === 'desktop' ? 'is-active' : ''} onClick={() => setPreviewMode('desktop')} aria-label="Desktop preview">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6"><rect x="2" y="3" width="20" height="14" rx="2" /><line x1="8" y1="21" x2="16" y2="21" /><line x1="12" y1="17" x2="12" y2="21" /></svg>
                </button>
                <button className={previewMode === 'mobile' ? 'is-active' : ''} onClick={() => setPreviewMode('mobile')} aria-label="Mobile preview">
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6"><rect x="5" y="2" width="14" height="20" rx="2" /><line x1="12" y1="18" x2="12.01" y2="18" /></svg>
                </button>
              </div>
            </div>

            <div className="eb-stage">
              {isV2 ? (
                <div className="eb-inbox" style={{ maxWidth: previewMode === 'mobile' ? '375px' : '600px' }} onClick={(e) => e.stopPropagation()}>
                  <input
                    className="eb-inbox-subject"
                    type="text"
                    value={subject}
                    onChange={(e) => { const v = e.target.value; setSubject(v) }}
                    placeholder="Subject line"
                    aria-label="Subject line"
                  />
                  <div className="eb-inbox-pre-row">
                    <input
                      className="eb-inbox-pre"
                      type="text"
                      value={preheader}
                      onChange={(e) => { const v = e.target.value; setPreheader(v) }}
                      placeholder="Preview text: the line inboxes show after the subject"
                      aria-label="Preview text"
                      maxLength={140}
                      style={{ paddingRight: preheader ? 52 : 16 }}
                    />
                    {preheader.length > 0 && <span className="eb-inbox-count">{preheader.length}/90</span>}
                  </div>
                </div>
              ) : (
                <input
                  className="eb-subject"
                  type="text"
                  value={subject}
                  onChange={(e) => setSubject(e.target.value)}
                  placeholder="Subject line"
                  onClick={(e) => e.stopPropagation()}
                  style={{ maxWidth: previewMode === 'mobile' ? '375px' : '100%' }}
                />
              )}
              <div className="eb-email-wrap" style={{ maxWidth: previewMode === 'mobile' ? '375px' : (isV2 ? '600px' : '100%') }}>
                <div className="eb-email" onClick={(e) => e.stopPropagation()}>
                  {emailBlocks.map((block, index) => (
                    <div
                      key={block.id}
                      data-bid={block.id}
                      className={[
                        'eb-blockwrap',
                        selectedBlockId === block.id ? 'is-selected' : '',
                        dragIndex === index ? 'is-dragging' : '',
                        dropIndex === index && dragIndex != null ? 'drop-before' : '',
                        dropIndex === index + 1 && dragIndex != null ? 'drop-after' : ''
                      ].filter(Boolean).join(' ')}
                      draggable={dragArmedId === block.id}
                      onDragStart={(e) => handleBlockDragStart(e, index)}
                      onDragOver={(e) => handleBlockDragOver(e, index)}
                      onDrop={handleBlockDrop}
                      onDragEnd={handleBlockDragEnd}
                      onMouseUp={() => { if (dragIndex == null) setDragArmedId(null) }}
                    >
                      {block.type !== 'footer' && (
                        <button
                          type="button"
                          className="eb-drag"
                          aria-label="Drag to reorder"
                          title="Drag to reorder"
                          onMouseDown={() => setDragArmedId(block.id)}
                          onMouseUp={() => setDragArmedId(null)}
                          onClick={(e) => e.stopPropagation()}
                        >
                          <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                            <circle cx="9" cy="5" r="1.6"/><circle cx="15" cy="5" r="1.6"/>
                            <circle cx="9" cy="12" r="1.6"/><circle cx="15" cy="12" r="1.6"/>
                            <circle cx="9" cy="19" r="1.6"/><circle cx="15" cy="19" r="1.6"/>
                          </svg>
                        </button>
                      )}
                      <BlockRenderer
                        block={block}
                        isEditable={true}
                        isSelected={selectedBlockId === block.id}
                        onSelect={handleBlockSelect}
                        previewMode={previewMode}
                        emailTheme={emailTheme}
                      />
                    </div>
                  ))}
                </div>
                {/* Action rail beside the selected block, outside the email card */}
                {selectedBlock && railTop != null && dragIndex == null && (() => {
                  const idx = emailBlocks.findIndex((b) => b.id === selectedBlock.id)
                  return (
                    <div className="eb-block-rail" style={{ top: `${railTop}px` }} onClick={(e) => e.stopPropagation()}>
                      <button type="button" className="eb-ctx-btn" onClick={() => moveBlock(selectedBlock.id, 'up')} disabled={idx <= 0} aria-label="Move up" title="Move up"><IconUp /></button>
                      <button type="button" className="eb-ctx-btn" onClick={() => moveBlock(selectedBlock.id, 'down')} disabled={idx >= emailBlocks.length - 1} aria-label="Move down" title="Move down"><IconDown /></button>
                      <button type="button" className="eb-ctx-btn" onClick={() => duplicateBlock(selectedBlock.id)} aria-label="Duplicate" title="Duplicate"><IconCopy /></button>
                      <button type="button" className="eb-ctx-btn eb-ctx-btn--danger" onClick={() => { deleteBlock(selectedBlock.id); setEditingBlockId(null) }} disabled={selectedBlock.type === 'footer'} aria-label="Delete" title={selectedBlock.type === 'footer' ? 'The footer carries your legally required unsubscribe link' : 'Delete'}><IconTrash /></button>
                    </div>
                  )
                })()}
              </div>
            </div>
          </div>
        </div>
        </>
      )}

      {/* Step 2: Audience */}
      {step === 2 && (
        <div style={{ maxWidth: '640px', margin: '0 auto', padding: '48px clamp(16px, 5vw, 40px) 140px' }}>
          <h2 style={{ fontSize: '24px', fontWeight: '600', color: '#1A1A1E', margin: '0 0 8px 0' }}>Who should receive this?</h2>
          {/* One combined audience, not a segments-vs-clients choice: pick a
              segment, hand-pick clients, or both. Either alone can send. */}
          <p style={{ fontSize: '14px', color: '#8E8E93', margin: user?.userInfo?.hairdresser?.isSalonOwner === 1 ? '0 0 6px 0' : '0 0 28px 0' }}>Pick a smart segment, hand-pick individual clients, or combine the two.</p>
          {user?.userInfo?.hairdresser?.isSalonOwner === 1 && (
            <p style={{ fontSize: '12.5px', color: '#8E8E93', margin: '0 0 28px 0' }}>Counts include the own clients of self-employed stylists who have let you run their marketing.</p>
          )}

          {/* Smart segments — tap again to deselect, since clients-only is valid */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
              {loadingAudience ? (
                [1, 2, 3, 4].map(i => (
                  <div key={i} style={{ height: '58px', borderRadius: '12px', backgroundColor: '#F2F2F7' }} />
                ))
              ) : audienceSegments.length === 0 ? (
                <div style={{ padding: '40px', textAlign: 'center', color: '#8E8E93' }}>
                  <div style={{ fontSize: '14px', fontWeight: '600', color: '#4B4B52', marginBottom: '6px' }}>No clients found</div>
                  <div style={{ fontSize: '13px' }}>Add clients to your salon to send campaigns</div>
                </div>
              ) : (
                audienceSegments.map((segment) => {
                  const isSelected = selectedSegments[0] === segment.id
                  return (
                    <button
                      key={segment.id}
                      type="button"
                      onClick={() => toggleSegment(segment.id)}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '12px',
                        width: '100%',
                        padding: '14px 16px',
                        backgroundColor: '#fff',
                        border: isSelected ? '2px solid #29282D' : '1px solid #ECECF0',
                        borderRadius: '12px',
                        cursor: 'pointer',
                        fontFamily: 'inherit',
                        transition: 'border-color 0.15s'
                      }}
                    >
                      <span style={{
                        width: '18px', height: '18px', borderRadius: '50%', flexShrink: 0,
                        border: isSelected ? '6px solid #29282D' : '2px solid #DEDEE2',
                        backgroundColor: '#fff', boxSizing: 'border-box'
                      }} />
                      <span style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: '2px', textAlign: 'left' }}>
                        <span style={{ fontSize: '14px', fontWeight: '500', color: '#1A1A1E' }}>{segment.name}</span>
                        {segment.description && (
                          <span style={{ fontSize: '12px', color: '#8E8E93' }}>{segment.description}</span>
                        )}
                      </span>
                      <span style={{
                        fontSize: '12.5px', fontWeight: '600', color: '#4B4B52',
                        backgroundColor: '#F2F2F7', borderRadius: '100px', padding: '4px 11px', flexShrink: 0
                      }}>
                        {(segment.count || 0).toLocaleString()}
                      </span>
                    </button>
                  )
                })
              )}
            </div>

          {/* Hand-picked clients: on top of a segment, or the whole audience */}
          <div style={{ marginTop: 28 }}>
            <p style={{ fontSize: '13.5px', fontWeight: 600, color: '#1A1A1E', margin: '0 0 4px' }}>
              Add specific clients {selectedSegments.length > 0 && <span style={{ fontWeight: 400, color: '#8E8E93' }}>(optional)</span>}
            </p>
            <p style={{ fontSize: '12.5px', color: '#8E8E93', margin: '0 0 12px' }}>
              {selectedSegments.length > 0
                ? 'Hand-pick clients to add on top of the segment, e.g. VIPs plus a few regulars.'
                : 'Hand-pick exactly who receives it, no segment needed.'}
            </p>
            <ClientPicker dispatch={dispatch} channel="email" selected={selectedClients} setSelected={setSelectedClients} />
          </div>
        </div>
      )}

      {/* Step 3: Review */}
      {step === 3 && (
        <>
        <div style={{ maxWidth: '600px', margin: '0 auto', padding: '48px clamp(16px, 5vw, 40px)', paddingBottom: '140px' }}>
          <h2 style={{ fontSize: '24px', fontWeight: '600', color: '#1a1a1a', margin: '0 0 32px 0' }}>Review & Send</h2>

          {isV2 && (
            <div className="eb-review-mail">
              <div className="eb-review-mail-head">
                <h3 style={{ fontSize: '12px', fontWeight: '600', color: '#9ca3af', margin: 0, textTransform: 'uppercase', letterSpacing: '0.5px' }}>Your email</h3>
                <div className="tp-device" role="group" aria-label="Preview size">
                  <button type="button" className={reviewDevice === 'desktop' ? 'is-on' : ''} onClick={() => setReviewDevice('desktop')} aria-label="Desktop preview">
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" aria-hidden><rect x="2" y="3" width="20" height="14" rx="2" /><line x1="8" y1="21" x2="16" y2="21" /><line x1="12" y1="17" x2="12" y2="21" /></svg>
                  </button>
                  <button type="button" className={reviewDevice === 'mobile' ? 'is-on' : ''} onClick={() => setReviewDevice('mobile')} aria-label="Phone preview">
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" aria-hidden><rect x="5" y="2" width="14" height="20" rx="2.5" /><line x1="11" y1="18" x2="13" y2="18" /></svg>
                  </button>
                </div>
              </div>
              <div className="eb-review-mail-stage" style={{ background: emailTheme.palette.canvas }}>
                <div className="eb-review-mail-frame" style={{ maxWidth: reviewDevice === 'mobile' ? 375 : 680 }}>
                  <EmailFrame key={reviewDevice} html={reviewHtml} width={reviewDevice === 'mobile' ? 375 : 680} title="Your email" />
                </div>
              </div>
              <ul className="eb-checks">
                {reviewChecks.length
                  ? reviewChecks.map(c => <li key={c}>{c}</li>)
                  : <li className="is-ok">Subject, links and images all look good.</li>}
              </ul>
            </div>
          )}
          
          <div style={{ backgroundColor: 'white', borderRadius: '12px', border: '1px solid #e5e5e5', padding: '24px', marginBottom: '20px' }}>
            <h3 style={{ fontSize: '12px', fontWeight: '600', color: '#9ca3af', margin: '0 0 16px 0', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Campaign Details</h3>
            <div style={{ display: 'grid', gap: '16px' }}>
              <div>
                <p style={{ fontSize: '12px', color: '#6b7280', margin: '0 0 4px 0' }}>Subject</p>
                <p style={{ fontSize: '15px', color: '#1a1a1a', margin: 0, fontWeight: '500' }}>{subject || 'No subject'}</p>
              </div>
              <div>
                <p style={{ fontSize: '12px', color: '#6b7280', margin: '0 0 4px 0' }}>Recipients</p>
                <p style={{ fontSize: '15px', color: '#1a1a1a', margin: 0, fontWeight: '500' }}>{audienceLabel()} · {getTotalAudienceSize().toLocaleString()} {getTotalAudienceSize() === 1 ? 'client' : 'clients'}</p>
                {selectedClients.length > 0 && reachableCount !== null && reachableCount < selectedClients.length && (
                  <p style={{ fontSize: 13, color: '#A05A1F', margin: '6px 0 0' }}>
                    {reachableCount === 0
                      ? "None of these clients can receive marketing email — they've unsubscribed or can't be reached, so nothing would send."
                      : `${reachableCount} of ${selectedClients.length} can receive marketing email — the rest have unsubscribed or can't be reached, and will be skipped (not billed).`}
                  </p>
                )}
              </div>
              <div>
                <p style={{ fontSize: '12px', color: '#6b7280', margin: '0 0 4px 0' }}>Blocks</p>
                <p style={{ fontSize: '15px', color: '#1a1a1a', margin: 0, fontWeight: '500' }}>{emailBlocks.length} content blocks</p>
              </div>
            </div>
          </div>

          {/* Marketing allowance — email is included up to the monthly pool, then
              draws on the message balance. Only a shortfall prompts a top-up. */}
          {emailSpendable !== null && (() => {
            const recips = getTotalAudienceSize()
            const short = recips > emailSpendable
            return (
              <div style={{ backgroundColor: 'white', borderRadius: '12px', border: `1px solid ${short ? '#F0D9B8' : '#e5e5e5'}`, padding: '24px', marginBottom: '20px' }}>
                <h3 style={{ fontSize: '12px', fontWeight: '600', color: '#9ca3af', margin: '0 0 16px 0', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Marketing allowance</h3>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
                  <span style={{ fontSize: '13px', color: '#6b7280' }}>Emails you can send now</span>
                  <span style={{ fontSize: '15px', fontWeight: 600, color: short ? '#A05A1F' : '#1a7f37' }}>{Number(emailSpendable).toLocaleString()}</span>
                </div>
                <p style={{ fontSize: '12px', color: '#8e8e93', margin: '8px 0 0', lineHeight: 1.5 }}>
                  Your free monthly allowance first, then your {formatPence(walletBalancePence(walletRow) || 0)} message balance ({formatUnitPence(messagePrices(walletRow).emailPence)} an email).
                </p>
                {short && (
                  <div style={{ marginTop: '14px', paddingTop: '14px', borderTop: '1px solid #F2F2F7' }}>
                    <p style={{ fontSize: '12.5px', color: '#A05A1F', margin: '0 0 10px', lineHeight: 1.5 }}>
                      This send is {recips.toLocaleString()} emails — {Number(recips - emailSpendable).toLocaleString()} beyond what’s covered. Top up to send to everyone.
                    </p>
                    <button type="button" onClick={() => setShowTopup(true)}
                      style={{ padding: '9px 16px', borderRadius: 10, border: '1px solid #007EA0', background: 'rgba(0,126,160,0.06)', color: '#007EA0', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>
                      Top up
                    </button>
                  </div>
                )}
              </div>
            )
          })()}

          {!isDesignMode && (
            <SendLocationsCard value={otherSiteIds} onChange={setOtherSiteIds} channel="email" disabled={isSending} />
          )}

          {showTopup && (
            <SmsWalletTopupModal
              balancePence={walletBalancePence(walletRow)}
              currentBalance={walletCredits}
              prices={messagePrices(walletRow)}
              creditCosts={creditCosts}
              autoRecharge={autoRecharge}
              onSuccess={loadAllowance}
              onClose={() => setShowTopup(false)}
            />
          )}

          {/* Sender identity — Fresha parity. Sends leave Beu's authenticated
              marketing domain; these two fields only change what recipients see. */}
          <div style={{ backgroundColor: 'white', borderRadius: '12px', border: '1px solid #e5e5e5', padding: '24px', marginBottom: '20px' }}>
            <h3 style={{ fontSize: '12px', fontWeight: '600', color: '#9ca3af', margin: '0 0 16px 0', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Sender identity</h3>
            <div style={{ display: 'grid', gap: '18px' }}>
              <div>
                <label htmlFor="cc-sender-name" style={{ display: 'block', fontSize: '13px', fontWeight: 600, color: '#1a1a1a', margin: '0 0 6px 0' }}>Sender name</label>
                <input
                  id="cc-sender-name"
                  type="text"
                  value={senderName}
                  onChange={(e) => setSenderName(e.target.value)}
                  placeholder={salonNameDefault || 'Your salon name'}
                  style={{ width: '100%', boxSizing: 'border-box', padding: '11px 14px', fontSize: '14px', color: '#1a1a1a', border: '1px solid #E5E5EA', borderRadius: '10px', outline: 'none', fontFamily: 'inherit', background: '#fff' }}
                />
                <p style={{ fontSize: '12px', color: '#8e8e93', margin: '6px 0 0', lineHeight: 1.5 }}>The name recipients see in their inbox. Leave blank to use your salon name.</p>
              </div>
              <div>
                <label htmlFor="cc-reply-to" style={{ display: 'block', fontSize: '13px', fontWeight: 600, color: '#1a1a1a', margin: '0 0 6px 0' }}>Reply-to email</label>
                <input
                  id="cc-reply-to"
                  type="email"
                  value={replyToEmail}
                  onChange={(e) => setReplyToEmail(e.target.value)}
                  placeholder={salonEmailDefault || 'replies@yoursalon.com'}
                  style={{ width: '100%', boxSizing: 'border-box', padding: '11px 14px', fontSize: '14px', color: '#1a1a1a', border: `1px solid ${replyToInvalid ? '#E0A54B' : '#E5E5EA'}`, borderRadius: '10px', outline: 'none', fontFamily: 'inherit', background: '#fff' }}
                />
                <p style={{ fontSize: '12px', color: replyToInvalid ? '#A05A1F' : '#8e8e93', margin: '6px 0 0', lineHeight: 1.5 }}>
                  {replyToInvalid
                    ? 'That doesn’t look like an email address — double-check it, or leave it blank.'
                    : (salonEmailDefault ? 'Where client replies land. Defaults to your salon email.' : 'Where client replies land. Replies go to your salon email by default.')}
                </p>
              </div>
            </div>
            <p style={{ fontSize: '12px', color: '#8e8e93', lineHeight: 1.55, margin: '16px 0 0', paddingTop: '14px', borderTop: '1px solid #F2F2F7' }}>
              Emails send from Beu’s secure marketing address, but recipients see this sender name and their replies come to you.
            </p>
          </div>

          <div style={{ backgroundColor: 'white', borderRadius: '12px', border: '1px solid #e5e5e5', padding: '24px', marginBottom: '20px' }}>
            <h3 style={{ fontSize: '12px', fontWeight: '600', color: '#9ca3af', margin: '0 0 16px 0', textTransform: 'uppercase', letterSpacing: '0.5px' }}>UK marketing compliance</h3>
            {(() => {
              const biz = loadBusinessDetails(localStorage.getItem('salonId'))
              const bizOk = isBusinessDetailsComplete(biz)
              const rows = [
                { ok: true, text: 'Unsubscribe and update-preferences links are added to every email automatically' },
                { ok: true, text: 'Your salon name and address are included in the footer to identify the sender' },
                { ok: true, text: 'Clients who have opted out of marketing are excluded at send' },
                bizOk
                  ? { ok: true, text: biz.legalForm === 'ltd' ? `Company registration details included: ${companyDetailsLine(biz)}` : 'Business details confirmed: trading as self-employed' }
                  : { ok: false, text: 'Tell us how your business is set up in Marketing Settings. Limited companies legally need their registered name, number and office on marketing emails.' },
              ]
              return rows.map((row) => (
                <div key={row.text} style={{ display: 'flex', alignItems: 'flex-start', gap: '10px', marginBottom: '10px' }}>
                  <span style={{
                    width: '18px', height: '18px', borderRadius: '50%', flexShrink: 0, marginTop: '1px',
                    backgroundColor: row.ok ? 'rgba(0,126,160,0.1)' : 'rgba(160,90,31,0.12)', display: 'inline-flex', alignItems: 'center', justifyContent: 'center'
                  }}>
                    {row.ok ? (
                      <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="#007EA0" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>
                    ) : (
                      <span style={{ fontSize: '11px', fontWeight: 800, color: '#A05A1F', lineHeight: 1 }}>!</span>
                    )}
                  </span>
                  <span style={{ fontSize: '13.5px', color: row.ok ? '#374151' : '#A05A1F', lineHeight: 1.5 }}>{row.text}</span>
                </div>
              ))
            })()}
            <p style={{ fontSize: '12px', color: '#8e8e93', lineHeight: 1.55, margin: '14px 0 0', paddingTop: '14px', borderTop: '1px solid #F2F2F7' }}>
              A reminder on the rules: market to your own clients about similar services (the soft opt-in), or with their consent.
            </p>
          </div>

        </div>

        {/* The final CTAs, pinned where the thumb is on mobile (the top-bar
            buttons were cramped there, and Save draft has the X + auto-name). */}
        {isMobileView && (
          <div style={{ position: 'fixed', left: 0, right: 0, bottom: 0, zIndex: 20, display: 'flex', gap: 10, padding: '12px 16px calc(12px + env(safe-area-inset-bottom, 0px))', background: '#fff', borderTop: '1px solid #ECECF0' }}>
            <button
              type="button"
              className="cf-btn-secondary"
              onClick={() => requireBusinessDetails(() => setShowScheduleModal(true))}
              disabled={isSending}
              style={{ flex: 1, height: 48, display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}
            >
              Schedule
            </button>
            <button
              type="button"
              className="cf-btn-primary"
              onClick={() => requireBusinessDetails(() => setShowSendConfirm(true))}
              disabled={isSending}
              style={{ flex: 1.4, height: 48, display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}
            >
              {isSending ? 'Sending…' : 'Send now'}
            </button>
          </div>
        )}
        </>
      )}

      {/* Send confirmation — the "cannot be undone" moment lives here, on the
          click, instead of as a checkbox buried at the bottom of the review. */}
      {siteFan && (
        <SendLocationsResults
          title={isScheduled ? 'Scheduled here' : 'Sent here'}
          results={siteFan.results}
          sites={sendSites.others}
          error={siteFan.error}
          retrying={siteFan.retrying}
          onRetry={() => { setSiteFan(f => ({ ...f, retrying: true })); runSiteFan(siteFan) }}
          onDone={() => { const after = siteFan.after; setSiteFan(null); if (after) after() }}
        />
      )}

      {showSendConfirm && (
        <div style={{ position: 'fixed', inset: 0, zIndex: 100011, background: 'rgba(26,26,30,0.45)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px' }} onClick={() => setShowSendConfirm(false)}>
          <div onClick={(e) => e.stopPropagation()} style={{ width: '100%', maxWidth: '420px', background: '#fff', borderRadius: '16px', padding: '26px' }}>
            <h2 style={{ fontSize: '19px', fontWeight: 700, color: '#1A1A1E', margin: '0 0 6px' }}>Send this campaign?</h2>
            <p style={{ fontSize: '14px', color: '#4B4B52', lineHeight: 1.6, margin: '0 0 20px' }}>
              It goes to <strong>{getTotalAudienceSize().toLocaleString()}</strong> {getTotalAudienceSize() === 1 ? 'recipient' : 'recipients'} straight away{chosenOtherSites().length ? ` (and from ${chosenOtherSites().length} other ${chosenOtherSites().length === 1 ? 'location' : 'locations'}, to their own clients)` : ''}, and sending cannot be undone.
            </p>
            <div style={{ display: 'flex', gap: '10px' }}>
              <button
                type="button"
                onClick={() => setShowSendConfirm(false)}
                style={{ flex: 1, padding: '12px', border: '1px solid #ECECF0', borderRadius: '10px', background: '#fff', fontSize: '14px', fontWeight: 600, color: '#1A1A1E', cursor: 'pointer', fontFamily: 'inherit' }}
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={() => { setShowSendConfirm(false); handleSend() }}
                style={{ flex: 1.3, padding: '12px', border: 'none', borderRadius: '10px', background: '#29282D', fontSize: '14px', fontWeight: 600, color: '#fff', cursor: 'pointer', fontFamily: 'inherit' }}
              >
                Send now
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modals */}
      {showBizGate && (
        <div style={{ position: 'fixed', inset: 0, zIndex: 100010, background: 'rgba(26,26,30,0.45)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px' }} onClick={() => setShowBizGate(false)}>
          <div onClick={(e) => e.stopPropagation()} style={{ width: '100%', maxWidth: '540px', maxHeight: '90vh', overflowY: 'auto', background: '#fff', borderRadius: '16px', padding: '28px' }}>
            <h2 style={{ fontSize: '20px', fontWeight: 700, color: '#1A1A1E', margin: '0 0 6px' }}>Confirm your business details first</h2>
            <p style={{ fontSize: '13.5px', color: '#8E8E93', lineHeight: 1.55, margin: '0 0 20px' }}>
              UK law requires marketing emails to identify the sender. Tell us how your business is set up and it appears in your footer automatically. You only do this once.
            </p>
            <BusinessDetailsForm
              value={bizDraft}
              onChange={(field, value) => setBizDraft(prev => ({ ...prev, [field]: value }))}
            />
            <div style={{ display: 'flex', gap: '10px', marginTop: '22px' }}>
              <button type="button" onClick={() => setShowBizGate(false)} style={{ flex: 1, padding: '12px', border: '1px solid #ECECF0', borderRadius: '10px', background: '#fff', fontSize: '14px', fontWeight: 600, color: '#1A1A1E', cursor: 'pointer', fontFamily: 'inherit' }}>
                Cancel
              </button>
              <button
                type="button"
                disabled={!isBusinessDetailsComplete(bizDraft)}
                onClick={() => {
                  saveBusinessDetails(localStorage.getItem('salonId'), bizDraft)
                  setShowBizGate(false)
                  if (afterBizGate) afterBizGate()
                }}
                style={{
                  flex: 1.4, padding: '12px', border: 'none', borderRadius: '10px',
                  background: isBusinessDetailsComplete(bizDraft) ? '#29282D' : '#C7C7CC',
                  fontSize: '14px', fontWeight: 600, color: '#fff',
                  cursor: isBusinessDetailsComplete(bizDraft) ? 'pointer' : 'default', fontFamily: 'inherit'
                }}
              >
                Save and continue
              </button>
            </div>
          </div>
        </div>
      )}

      {showScheduleModal && (
        <ScheduleModal 
          recipientCount={getTotalAudienceSize()}
          onSchedule={handleSchedule}
          onClose={() => setShowScheduleModal(false)}
        />
      )}
      
      </div>
    </CreatorFrame>
  )
}
const mapStateToProps = (state) => ({ user: state.user })
export const CampaignComposer = connect(mapStateToProps)(CampaignComposerComponent)
